import GigShareDialog from "@/components/GigShareDialog";
import { useState, useEffect, useRef } from "react";
import { openExternalLink } from "@/lib/utils";
import { useNavigate, useParams, useLocation, useSearchParams } from "react-router-dom";
import { Layout, GigCard } from "@/pages/Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ClearableInput } from "@/components/ui/clearable-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Calendar as CalendarIcon, CalendarPlus, Search, Trash2, ArrowLeft, Clock, History, Sparkles, Loader2, Share2, Copy, MessageCircle, Check, Link as LinkIcon, Mic, Users, User, SlidersHorizontal, X, Car, ChevronDown, LayoutTemplate, Pencil, GripVertical } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { apiClient, API } from "@/App";
import { toast } from "sonner";
import { format, parseISO, isAfter, isBefore, startOfToday } from "date-fns";
import { cn } from "@/lib/utils";
import { copyToClipboard } from "@/lib/clipboard";
import { gigLocalDate } from "@/utils/dateUtils";
import { SetlistEditor } from "@/components/SetlistEditor";
import { Switch } from "@/components/ui/switch";
import { SearchableSelect } from "@/components/SearchableSelect";
import { FirstGigSuccess } from "@/components/FirstGigSuccess";
import { SmartFillScheduleReview } from "@/components/SmartFillScheduleReview";
import { Kicker } from "@/design-system/primitives";
import { tokens } from "@/design-system/tokens";
import { useIsDesktop } from "@/hooks/useIsDesktop";

// Mileage phases that cover a there-and-back drive (×2). All other phases are
// single legs (×1). Used by the mileage calculator on multi-gig / fly-out days.
// Regular places (Settings → Travel) add legs keyed "place_rt:<id>",
// "place_out:<id>" and "place_ret:<id>"; place_rt is also a round trip.
const MILEAGE_ROUND_TRIP_PHASES = new Set(["round_trip", "airport_round_trip"]);
const parsePlacePhase = (phase) => {
  const m = /^place_(rt|out|ret):(.+)$/.exec(phase || "");
  return m ? { leg: m[1], placeId: m[2] } : null;
};
// Mileage phases are stored as one string. The calculator shows them as two
// choices: where (venue, airport or a regular place) and direction.
const phaseWhere = (phase) => {
  const pp = parsePlacePhase(phase);
  if (pp) return `place:${pp.placeId}`;
  return phase?.startsWith("airport_") ? "airport" : "venue";
};
const phaseDirection = (phase) => {
  const pp = parsePlacePhase(phase);
  if (pp) return { rt: "round_trip", out: "outbound", ret: "return" }[pp.leg];
  return phase?.replace(/^airport_/, "") || "round_trip";
};
const composePhase = (where, direction) => {
  if (where.startsWith("place:")) {
    const leg = { round_trip: "rt", outbound: "out", return: "ret" }[direction] || "rt";
    return `place_${leg}:${where.slice("place:".length)}`;
  }
  if (where === "airport") {
    return direction === "outbound" || direction === "return" ? `airport_${direction}` : "airport_round_trip";
  }
  return direction;
};
const mileagePhaseMultiplier = (phase) =>
  (MILEAGE_ROUND_TRIP_PHASES.has(phase) || parsePlacePhase(phase)?.leg === "rt" ? 2 : 1);
// HMRC's approved mileage allowance rate for cars/vans (first 10,000 miles/year).
const HMRC_MILEAGE_RATE = 0.45;

// ── Draggable "drop-down" blocks that sit below The Essentials ──
// Styled like the Schedule / Logistics disclosures. Order + open state persist
// per-user in localStorage. Drag is handle-only (lockable): the title toggles
// open/closed; the grip reorders. Mirrors the desktop dashboard widget DnD
// (PointerSensor with an 8px activation distance = smooth, no accidental drags).
const GIG_PILL_ORDER_KEY = "fretless:gig-pills:order:v2";
const GIG_PILL_OPEN_KEY = "fretless:gig-pills:open:v2";
const DEFAULT_PILL_ORDER = ["schedule", "logistics", "contacts", "transport", "money", "tickets", "setlist", "notes"];
const PILL_META = {
  schedule:  { title: "Schedule",              accent: "border-amber-200/50 bg-amber-50/30 dark:bg-amber-950/20 dark:border-amber-900/30" },
  logistics: { title: "Logistics",             accent: "border-blue-200/50 bg-blue-50/30 dark:bg-blue-950/20 dark:border-blue-900/30" },
  contacts:  { title: "Contacts & Invitations", accent: "border-violet-200/50 bg-violet-50/30 dark:bg-violet-950/20 dark:border-violet-900/30" },
  transport: { title: "Transport",             accent: "border-teal-200/50 bg-teal-50/30 dark:bg-teal-950/20 dark:border-teal-900/30" },
  money:     { title: "Money",                 accent: "border-emerald-200/50 bg-emerald-50/30 dark:bg-emerald-950/20 dark:border-emerald-900/30" },
  tickets:   { title: "Tickets",               accent: "border-sky-200/50 bg-sky-50/30 dark:bg-sky-950/20 dark:border-sky-900/30" },
  setlist:   { title: "Setlist",               accent: "border-fuchsia-200/50 bg-fuchsia-50/30 dark:bg-fuchsia-950/20 dark:border-fuchsia-900/30" },
  notes:     { title: "Notes",                 accent: "border-slate-300/50 bg-slate-50/40 dark:bg-slate-800/20 dark:border-slate-700/40" },
};

function SortablePill({ id, open, onToggle, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const meta = PILL_META[id] || { title: id, accent: "border-border" };
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
    position: "relative",
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-xl p-4 md:p-5 space-y-4 group ${meta.accent}`}
      data-testid={`pill-${id}`}
    >
      <div className="flex items-center justify-between w-full gap-2">
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center gap-2 text-left flex-1 min-w-0"
          data-testid={`pill-toggle-${id}`}
        >
          <Label className="cursor-pointer">{meta.title}</Label>
          <span className="text-xs text-muted-foreground">{open ? "−" : "+"}</span>
        </button>
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 rounded hover:bg-secondary md:opacity-0 group-hover:opacity-100 transition-opacity touch-none shrink-0"
          title="Drag to reorder"
          aria-label={`Reorder ${meta.title}`}
          data-testid={`pill-handle-${id}`}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
      </div>
      {open && <div className="space-y-4">{children}</div>}
    </div>
  );
}

// Each phase group has its own one-way distance: home↔venue legs share the
// gig's estimated_distance_miles, home↔airport legs share
// estimated_airport_distance_miles. "between" (venue-to-venue, multi-gig
// days) has no estimate — the user has to enter it by hand. Regular-place
// legs use that place's saved one-way miles from home.
const MILEAGE_AIRPORT_PHASES = new Set(["airport_round_trip", "airport_outbound", "airport_return"]);
const mileagePhaseDistance = (phase, formData, regularPlaces = []) => {
  if (phase === "between") return "";
  const placePhase = parsePlacePhase(phase);
  if (placePhase) {
    const place = regularPlaces.find((p) => p.id === placePhase.placeId);
    return place?.miles > 0 ? String(place.miles) : "";
  }
  const miles = MILEAGE_AIRPORT_PHASES.has(phase)
    ? formData.estimated_airport_distance_miles
    : formData.estimated_distance_miles;
  return miles > 0 ? String(miles) : "";
};

// Bucket gigs by month (same grouped-list pattern as the alphabetised
// collaborators/venues/bands lists in Scene.jsx, bucketed by month instead
// of first letter). The list is already date-sorted before this runs, so
// buckets come out in that same chronological order — ascending for
// "upcoming" (oldest month first), descending for "past" — with no extra
// key-sorting needed. Gigs with no/invalid date land in their own bucket
// rather than being silently dropped.
function groupGigsByMonth(gigsList) {
  const buckets = new Map();
  gigsList.forEach((gig) => {
    const localDateStr = gig.date ? gigLocalDate(gig.date) : null;
    const key = localDateStr ? localDateStr.slice(0, 7) : "no-date"; // "YYYY-MM"
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(gig);
  });
  return Array.from(buckets.entries()).map(([key, gigsInMonth]) => {
    const label = key === "no-date" ? "No date" : format(new Date(key + "-01T00:00:00"), "MMMM yyyy").toUpperCase();
    return [key, label, gigsInMonth];
  });
}

// Renders month-grouped sections with a heading + a card grid per group —
// visually the same shape as Scene.jsx's AlphaGrid (letter heading, divider
// line, count badge), just keyed by month instead of by letter.
function MonthGroupedGigs({ groups, renderItem, columns = "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.xxl }}>
      {groups.map(([key, label, gigsInMonth]) => (
        <section key={key} data-testid={`month-section-${key}`}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.space.md,
              marginBottom: tokens.space.md,
            }}
          >
            <span
              style={{
                fontFamily: tokens.font.mono,
                fontSize: 20,
                fontWeight: 600,
                letterSpacing: "0.08em",
                color: tokens.color.orange,
                lineHeight: 1,
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </span>
            <div aria-hidden="true" style={{ flex: 1, height: 1, background: tokens.color.divider }} />
            <span
              aria-hidden="true"
              style={{
                fontFamily: tokens.font.mono,
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "0.16em",
                color: tokens.color.textMuted,
              }}
            >
              {gigsInMonth.length}
            </span>
          </div>
          <div className={columns}>
            {gigsInMonth.map(renderItem)}
          </div>
        </section>
      ))}
    </div>
  );
}

const formatTimeInput = (value) => {
  if (!value) return value;
  const digits = value.replace(/\D/g, '');
  let hours, minutes;
  if (digits.length === 3) {
    hours = parseInt(digits[0], 10);
    minutes = parseInt(digits.slice(1), 10);
    if (hours > 23 || minutes > 59) return value;
    return `0${digits[0]}:${digits.slice(1)}`;
  }
  if (digits.length === 4) {
    hours = parseInt(digits.slice(0, 2), 10);
    minutes = parseInt(digits.slice(2), 10);
    if (hours > 23 || minutes > 59) return value;
    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  }
  if (digits.length === 2) {
    hours = parseInt(digits, 10);
    if (hours > 23) return value;
    return `${digits}:00`;
  }
  return value;
};

const EMPTY_FORM = {
  artist_name: "",
  venue_name: "",
  city: "",
  country: "UK",
  date: null,
  end_date: null,
  start_time: "",
  end_time: "",
  ticket_link: "",
  ticket_price: "",
  venue_website: "",
  venue_address: "",
  status: "confirmed",
  genre: "jazz",
  // Visibility - public gigs go on Songkick/Bandsintown
  is_public: true,
  gig_type: "public",  // public, function, session, rehearsal
  // Setlist & Notes
  setlist: [],
  gig_notes: "",
  call_time: "",
  dress_code: "",
  // Running order for the day — [{ label, time: "HH:MM" }]
  schedule: [],
  // Logistics
  accommodation: { name: "", maps_link: "", distance_from_venue: "", notes: "" },
  per_diems: { breakfast: { value: "", quantity: "" }, lunch: { value: "", quantity: "" }, dinner: { value: "", quantity: "" } },
  // Multi-day / international travel
  is_multiday: false,
  travel_date: null,
  departure_time: "",
  return_date: null,
  return_time: "",
  airport_buffer: "",
  departure_airport: null,
  destination_airport: null,
  return_airport: null,
  // Transport
  transport_type: "",  // driving, public_transport, pickup
  transport_notes: "",
  // Band & Performers
  band_id: "",
  performing_member_ids: [],
  // Collaborators (legacy - for non-band gigs)
  collaborator_ids: [],
  // Invitations (availability RSVP)
  invitations: [],
  // Revenue tracking
  fee: "",
  expenses: "",
  fee_currency: "GBP",
  fee_status: "pending",
  // Per-gig invoice recipient override
  invoice_recipient_type: "auto",
  invoice_recipient_id: "",
  invoice_recipient_name: "",
  invoice_recipient_email: "",
  // Recurring
  is_recurring: false,
  recurrence_pattern: "",
  recurrence_day: "",
  recurrence_end_date: null,
  // Tour
  tour_id: ""
};

// Preset labels for the gig-day running order (Schedule section).
const SCHEDULE_PRESETS = ["Arrival", "Soundcheck", "Downtime", "Doors", "Performance", "Finish", "Load out"];

// ───── Duplicate / template dialog ────────────────────────────────────
// Two modes:
//   1. New dates — pick N future dates, create N new gigs cloned from the
//      source (POST /gigs xN).
//   2. Apply to existing — pick existing gigs, overwrite their empty
//      fields with the source's details (PUT /gigs/:id xN).
// Cloned fields exclude gig_id, date (varies per copy), share_*, and
// fee_status (resets to "unpaid" for new copies).
const TEMPLATE_FIELDS = [
  "artist_name", "venue_name", "venue_address", "venue_lat", "venue_lng",
  "city", "country", "call_time", "start_time", "dress_code", "fee",
  "description", "genre", "ticket_link", "ticket_price", "status",
];

const MONTH_NAMES = {
  jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2, apr: 3, april: 3,
  may: 4, jun: 5, june: 5, jul: 6, july: 6, aug: 7, august: 7,
  sep: 8, sept: 8, september: 8, oct: 9, october: 9, nov: 10, november: 10,
  dec: 11, december: 11,
};

// Build an ISO "YYYY-MM-DD" from parts, rejecting anything Date() would
// silently roll over (e.g. 31 Feb -> 3 Mar).
function isoFromParts(year, month, day) {
  if (!year || !month || !day || month < 1 || month > 12 || day < 1 || day > 31) return null;
  const dt = new Date(year, month - 1, day);
  if (dt.getMonth() !== month - 1 || dt.getDate() !== day) return null;
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

// When a pasted date omits the year, default to the current year — but if
// that date has already passed, assume next year instead (so pasting a
// January date near December rolls forward rather than landing in the past).
function rollYear(month, day, year, today) {
  const candidate = new Date(year, month - 1, day);
  return candidate < today ? year + 1 : year;
}

// Parse one free-text date token into an ISO string, or null if it can't be
// read. Numeric d/m/y is treated as day-first (UK convention), matching how
// the rest of the app formats dates.
function parseDateToken(raw) {
  const token = (raw || "").trim().replace(/^[-*•]\s*/, "");
  if (!token) return null;

  const today = startOfToday();
  const currentYear = today.getFullYear();

  let m = token.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (m) return isoFromParts(+m[1], +m[2], +m[3]);

  m = token.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})$/);
  if (m) {
    const day = +m[1];
    const month = +m[2];
    const year = m[3].length === 2 ? 2000 + parseInt(m[3], 10) : parseInt(m[3], 10);
    return isoFromParts(year, month, day);
  }

  m = token.match(/^(\d{1,2})[/\-.](\d{1,2})$/);
  if (m) {
    const day = +m[1];
    const month = +m[2];
    return isoFromParts(rollYear(month, day, currentYear, today), month, day);
  }

  m = token.match(/^(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]+)\.?,?\s*(\d{4})?$/);
  if (m) {
    const month = MONTH_NAMES[m[2].toLowerCase()];
    if (month == null) return null;
    const day = +m[1];
    const year = m[3] ? +m[3] : rollYear(month + 1, day, currentYear, today);
    return isoFromParts(year, month + 1, day);
  }

  m = token.match(/^([A-Za-z]+)\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s*(\d{4})?$/);
  if (m) {
    const month = MONTH_NAMES[m[1].toLowerCase()];
    if (month == null) return null;
    const day = +m[2];
    const year = m[3] ? +m[3] : rollYear(month + 1, day, currentYear, today);
    return isoFromParts(year, month + 1, day);
  }

  return null;
}

function DuplicateGigDialog({ source, template, open, onClose, allGigs, onRefetch }) {
  const [mode, setMode] = useState("dates");
  const [dates, setDates] = useState([]);            // ["YYYY-MM-DD", ...]
  const [newDate, setNewDate] = useState("");
  const [pasteText, setPasteText] = useState("");
  const [weeklyCount, setWeeklyCount] = useState(12);
  const [selectedTargets, setSelectedTargets] = useState(() => new Set());
  const [targetFilter, setTargetFilter] = useState("");
  const [overwriteAll, setOverwriteAll] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setMode("dates");
      setDates([]);
      setNewDate("");
      setPasteText("");
      setWeeklyCount(12);
      setSelectedTargets(new Set());
      setTargetFilter("");
      setOverwriteAll(false);
    }
  }, [open]);

  // Two sources feed this dialog: an existing gig ("Duplicate" — clone a
  // narrower field set via TEMPLATE_FIELDS) or a saved template (already the
  // full reusable-preset field set from TEMPLATE_GIG_FIELDS on the backend).
  const isTemplateMode = !!template;

  if (!source && !template) return null;

  // Build cloned-field payload from whichever source is active.
  const buildClone = () => {
    const out = {};
    if (isTemplateMode) {
      Object.entries(template.fields || {}).forEach(([f, v]) => {
        if (v != null && v !== "") out[f] = v;
      });
      return out;
    }
    TEMPLATE_FIELDS.forEach((f) => {
      if (source[f] != null && source[f] !== "") out[f] = source[f];
    });
    return out;
  };

  const addDate = () => {
    if (!newDate || dates.includes(newDate)) return;
    setDates((prev) => [...prev, newDate].sort());
    setNewDate("");
  };

  const removeDate = (d) => setDates((prev) => prev.filter((x) => x !== d));

  // "Add weekly" helper — appends N weekly occurrences starting from the
  // anchor (last date in the list, otherwise the source gig's date; templates
  // have no date of their own, so this needs at least one date added first).
  const addWeekly = () => {
    const n = Math.max(1, Math.min(52, parseInt(weeklyCount, 10) || 0));
    if (!n) return;
    const anchor = dates.length > 0 ? dates[dates.length - 1] : (isTemplateMode ? null : source.date);
    if (!anchor) return;
    const out = [...dates];
    const base = new Date(anchor + "T00:00:00");
    for (let i = 1; i <= n; i++) {
      const d = new Date(base);
      d.setDate(d.getDate() + i * 7);
      const iso = d.toISOString().slice(0, 10);
      if (!out.includes(iso)) out.push(iso);
    }
    out.sort();
    setDates(out);
  };

  // Add a batch of dates pasted as free text — one per line, or separated by
  // commas/semicolons. Irregular schedules (a run of gig dates with no fixed
  // interval) are the point: paste the whole list instead of picking each
  // date individually. Unparseable tokens are reported so nothing silently
  // drops off the list.
  const addPastedDates = () => {
    const tokens = pasteText.split(/[\n,;]+/).map((t) => t.trim()).filter(Boolean);
    if (tokens.length === 0) return;
    const parsed = [];
    const failed = [];
    tokens.forEach((t) => {
      const iso = parseDateToken(t);
      if (iso) parsed.push(iso);
      else failed.push(t);
    });
    if (parsed.length > 0) {
      setDates((prev) => Array.from(new Set([...prev, ...parsed])).sort());
      toast.success(`Added ${parsed.length} date${parsed.length === 1 ? "" : "s"}`);
    }
    if (failed.length > 0) {
      toast.error(`Couldn't read: ${failed.join(", ")}`);
    }
    setPasteText(failed.join("\n"));
  };

  const toggleTarget = (id) => {
    setSelectedTargets((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Candidate targets: all gigs except the source itself (templates aren't a
  // gig, so nothing to exclude), optionally filtered by a venue/city/artist
  // substring match.
  const candidates = (allGigs || [])
    .filter((g) => isTemplateMode || g.gig_id !== source.gig_id)
    .filter((g) => {
      if (!targetFilter.trim()) return true;
      const q = targetFilter.trim().toLowerCase();
      return [g.venue_name, g.city, g.artist_name]
        .filter(Boolean)
        .some((s) => s.toLowerCase().includes(q));
    });

  const handleCreate = async () => {
    if (dates.length === 0) return;
    setSubmitting(true);
    const clone = buildClone();
    const results = await Promise.allSettled(
      dates.map((date) =>
        apiClient.post("/gigs", {
          ...clone,
          date,
          share_enabled: false,
          fee_status: "unpaid",
        })
      )
    );
    const ok = results.filter((r) => r.status === "fulfilled").length;
    setSubmitting(false);
    if (ok === dates.length) toast.success(`Created ${ok} gig${ok === 1 ? "" : "s"}`);
    else toast.error(`Created ${ok} of ${dates.length} — some failed`);
    if (onRefetch) onRefetch();
    onClose();
  };

  const handleApply = async () => {
    const ids = Array.from(selectedTargets);
    if (ids.length === 0) return;
    setSubmitting(true);
    const clone = buildClone();
    const targets = (allGigs || []).filter((g) => ids.includes(g.gig_id));
    const results = await Promise.allSettled(
      targets.map((target) => {
        const updates = {};
        Object.keys(clone).forEach((k) => {
          const hasValue = target[k] != null && target[k] !== "";
          if (overwriteAll || !hasValue) updates[k] = clone[k];
        });
        return apiClient.put(`/gigs/${target.gig_id}`, updates);
      })
    );
    const ok = results.filter((r) => r.status === "fulfilled").length;
    setSubmitting(false);
    if (ok === ids.length) toast.success(`Applied to ${ok} gig${ok === 1 ? "" : "s"}`);
    else toast.error(`Applied to ${ok} of ${ids.length} — some failed`);
    if (onRefetch) onRefetch();
    onClose();
  };

  const tk = tokens;
  const sourceLabel = isTemplateMode ? template.name : (source.artist_name || source.venue_name || "Gig");
  const canSubmit =
    !submitting &&
    ((mode === "dates" && dates.length > 0) ||
      (mode === "apply" && selectedTargets.size > 0));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !submitting && onClose()}>
      <DialogContent style={{ maxWidth: 520 }} data-testid="duplicate-gig-dialog">
        <DialogHeader>
          <DialogTitle>{isTemplateMode ? `New gigs from "${sourceLabel}"` : `Duplicate "${sourceLabel}"`}</DialogTitle>
          <DialogDescription>
            {isTemplateMode
              ? "Create gigs from this template on multiple dates, or fill in existing placeholder gigs."
              : "Use this gig as a template — copy its details onto new dates, or fill in existing placeholder gigs."}
          </DialogDescription>
        </DialogHeader>

        {/* Mode toggle */}
        <div
          style={{
            display: "inline-flex",
            background: tk.color.innerBg,
            border: tk.border.inner,
            borderRadius: tk.radius.md + 2,
            padding: 3,
            alignSelf: "flex-start",
          }}
        >
          {[
            { id: "dates", label: "New dates" },
            { id: "apply", label: "Apply to existing" },
          ].map((opt) => {
            const on = mode === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setMode(opt.id)}
                data-testid={`duplicate-mode-${opt.id}`}
                style={{
                  padding: "7px 12px",
                  background: on ? tokens.color.segActive : "transparent",
                  color: on ? tk.color.text : tk.color.textMuted,
                  border: "none",
                  borderRadius: 7,
                  cursor: "pointer",
                  fontFamily: tk.font.body,
                  fontSize: 12,
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* ─── Mode: New dates ─── */}
        {mode === "dates" && (
          <div style={{ display: "flex", flexDirection: "column", gap: tk.space.md }}>
            <div style={{ display: "flex", gap: tk.space.sm }}>
              <Input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                style={{ flex: 1 }}
                data-testid="duplicate-date-input"
              />
              <button
                type="button"
                onClick={addDate}
                disabled={!newDate}
                data-testid="duplicate-date-add"
                style={{
                  padding: "8px 14px",
                  background: tk.color.orange,
                  color: tokens.color.onOrange,
                  border: tk.border.brand,
                  borderRadius: tk.radius.md + 1,
                  fontFamily: tk.font.body,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: newDate ? "pointer" : "not-allowed",
                  opacity: newDate ? 1 : 0.5,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                + Add
              </button>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: tk.space.sm, flexWrap: "wrap" }}>
              <span style={{ ...tk.type.bodySm, color: tk.color.textDim }}>
                Or add weekly for
              </span>
              <Input
                type="number"
                min="1"
                max="52"
                value={weeklyCount}
                onChange={(e) => setWeeklyCount(e.target.value)}
                style={{ width: 64 }}
              />
              <span style={{ ...tk.type.bodySm, color: tk.color.textDim }}>
                weeks
              </span>
              <button
                type="button"
                onClick={addWeekly}
                data-testid="duplicate-weekly-add"
                style={{
                  padding: "6px 12px",
                  background: "transparent",
                  color: tk.color.textDim,
                  border: tk.border.card,
                  borderRadius: tk.radius.md,
                  fontFamily: tk.font.body,
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: "pointer",
                }}
              >
                + Add weekly
              </button>
            </div>

            {/* Paste a whole list of irregular dates at once, instead of
                adding them one at a time — the point for a run of gigs with
                no fixed interval. Accepts one per line or comma/semicolon
                separated; year is optional (rolls to next year if the
                resulting date has already passed). */}
            <div style={{ display: "flex", flexDirection: "column", gap: tk.space.xs }}>
              <span style={{ ...tk.type.bodySm, color: tk.color.textDim }}>
                Or paste a list of dates (one per line, or comma-separated)
              </span>
              <textarea
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                placeholder={"15 Jul\n22 Aug\n5 Sept, 19 Sept"}
                rows={3}
                data-testid="duplicate-dates-paste"
                style={{
                  width: "100%",
                  resize: "vertical",
                  padding: "8px 10px",
                  background: tk.color.cardBg,
                  border: tk.border.inner,
                  borderRadius: tk.radius.md,
                  fontFamily: tk.font.mono,
                  fontSize: 12.5,
                  color: tk.color.text,
                }}
              />
              <button
                type="button"
                onClick={addPastedDates}
                disabled={!pasteText.trim()}
                data-testid="duplicate-dates-paste-add"
                style={{
                  alignSelf: "flex-start",
                  padding: "6px 12px",
                  background: "transparent",
                  color: tk.color.textDim,
                  border: tk.border.card,
                  borderRadius: tk.radius.md,
                  fontFamily: tk.font.body,
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: pasteText.trim() ? "pointer" : "not-allowed",
                  opacity: pasteText.trim() ? 1 : 0.5,
                }}
              >
                + Add pasted dates
              </button>
            </div>

            {/* Selected-dates list */}
            <div
              style={{
                background: tk.color.innerBg,
                border: tk.border.inner,
                borderRadius: tk.radius.lg,
                padding: tk.space.md,
                minHeight: 100,
                maxHeight: 220,
                overflowY: "auto",
              }}
            >
              {dates.length === 0 ? (
                <p style={{ ...tk.type.bodySm, color: tk.color.textMuted, margin: 0, textAlign: "center" }}>
                  No dates added yet
                </p>
              ) : (
                <div style={{ display: "flex", flexWrap: "wrap", gap: tk.space.xs }}>
                  {dates.map((d) => (
                    <span
                      key={d}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: tk.space.xs,
                        padding: "4px 8px",
                        background: tk.color.cardBg,
                        border: tk.border.inner,
                        borderRadius: tk.radius.sm,
                        fontFamily: tk.font.mono,
                        fontSize: 12,
                        color: tk.color.text,
                      }}
                    >
                      {format(parseISO(d), "EEE d MMM yyyy")}
                      <button
                        type="button"
                        onClick={() => removeDate(d)}
                        aria-label={`Remove ${d}`}
                        style={{
                          background: "transparent",
                          border: "none",
                          color: tk.color.textMuted,
                          cursor: "pointer",
                          padding: 0,
                          display: "inline-flex",
                        }}
                      >
                        <X style={{ width: 12, height: 12 }} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── Mode: Apply to existing ─── */}
        {mode === "apply" && (
          <div style={{ display: "flex", flexDirection: "column", gap: tk.space.md }}>
            <Input
              type="text"
              placeholder="Filter by venue, city, or artist…"
              value={targetFilter}
              onChange={(e) => setTargetFilter(e.target.value)}
              data-testid="duplicate-target-filter"
            />
            <div
              style={{
                background: tk.color.innerBg,
                border: tk.border.inner,
                borderRadius: tk.radius.lg,
                padding: tk.space.xs,
                maxHeight: 260,
                overflowY: "auto",
              }}
            >
              {candidates.length === 0 ? (
                <p style={{ ...tk.type.bodySm, color: tk.color.textMuted, margin: 0, padding: tk.space.md, textAlign: "center" }}>
                  No matching gigs
                </p>
              ) : (
                candidates.map((g) => {
                  const checked = selectedTargets.has(g.gig_id);
                  return (
                    <label
                      key={g.gig_id}
                      data-testid={`duplicate-target-${g.gig_id}`}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: tk.space.sm,
                        padding: `${tk.space.sm}px ${tk.space.md}px`,
                        cursor: "pointer",
                        borderRadius: tk.radius.sm,
                        background: checked ? tk.color.cardBg : "transparent",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleTarget(g.gig_id)}
                        style={{ accentColor: tk.color.orange, width: 16, height: 16 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            ...tk.type.bodySm,
                            fontWeight: 500,
                            color: tk.color.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {g.artist_name || g.venue_name || "Untitled"}
                          {g.venue_name && g.artist_name ? ` @ ${g.venue_name}` : ""}
                        </div>
                        <div style={{ fontFamily: tk.font.mono, fontSize: 11, color: tk.color.textDim }}>
                          {g.date ? format(parseISO(g.date), "EEE d MMM yyyy") : "No date"}
                        </div>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: tk.space.sm,
                ...tk.type.bodySm,
                color: tk.color.textDim,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={overwriteAll}
                onChange={(e) => setOverwriteAll(e.target.checked)}
                style={{ accentColor: tk.color.orange, width: 16, height: 16 }}
              />
              Overwrite all fields (replace existing data — default is to fill only empty fields)
            </label>
          </div>
        )}

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            style={{
              padding: "10px 16px",
              background: "transparent",
              color: tk.color.textMuted,
              border: tk.border.card,
              borderRadius: tk.radius.md + 1,
              fontFamily: tk.font.body,
              fontSize: 13,
              fontWeight: 500,
              cursor: submitting ? "not-allowed" : "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={mode === "dates" ? handleCreate : handleApply}
            disabled={!canSubmit}
            data-testid="duplicate-submit"
            style={{
              padding: "10px 16px",
              background: tk.color.orange,
              color: tokens.color.onOrange,
              border: tk.border.brand,
              borderRadius: tk.radius.md + 1,
              fontFamily: tk.font.body,
              fontSize: 13,
              fontWeight: 600,
              cursor: canSubmit ? "pointer" : "not-allowed",
              opacity: canSubmit ? 1 : 0.5,
              whiteSpace: "nowrap",
            }}
          >
            {submitting
              ? "Saving…"
              : mode === "dates"
              ? `Create ${dates.length} gig${dates.length === 1 ? "" : "s"}`
              : `Apply to ${selectedTargets.size} gig${selectedTargets.size === 1 ? "" : "s"}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ───── Empty-state card (token-driven, shared across upcoming/past tabs) ──
function EmptyGigState({ icon: Icon, message, showCta = false, ctaLabel, ctaTestId, onCta }) {
  return (
    <div
      style={{
        background: tokens.color.cardBg,
        border: tokens.border.card,
        borderRadius: tokens.radius.xl,
        padding: `${tokens.space.xxxl}px ${tokens.space.xl}px`,
        textAlign: "center",
      }}
    >
      {Icon && (
        <Icon
          style={{
            width: 48,
            height: 48,
            color: tokens.color.textMuted,
            margin: "0 auto",
            marginBottom: tokens.space.lg,
          }}
        />
      )}
      <p
        style={{
          margin: 0,
          marginBottom: showCta ? tokens.space.lg : 0,
          ...tokens.type.body,
          color: tokens.color.textMuted,
        }}
      >
        {message}
      </p>
      {showCta && (
        <button
          type="button"
          onClick={onCta}
          data-testid={ctaTestId}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: tokens.space.sm,
            padding: "10px 16px",
            background: tokens.color.orange,
            color: tokens.color.onOrange,
            border: tokens.border.brand,
            borderRadius: tokens.radius.md + 1,
            fontFamily: tokens.font.body,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          <Plus className="h-4 w-4" />
          {ctaLabel}
        </button>
      )}
    </div>
  );
}

// Reuses /maps/places/autocomplete + /maps/places/details (same API as venue
// picker) but stores {name, place_id, address} without saving to the venues DB.
function AirportPicker({ value, onChange, label, placeholder, testId }) {
  const [query, setQuery] = useState(value?.name || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const justSelectedRef = useRef(false);

  useEffect(() => { setQuery(value?.name || ""); }, [value?.name]);

  useEffect(() => {
    if (justSelectedRef.current) { justSelectedRef.current = false; return; }
    if (query.length < 2 || query === (value?.name || "")) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await apiClient.get(`/maps/places/autocomplete?q=${encodeURIComponent(query)}`);
        const preds = res.data?.predictions || [];
        setResults(preds);
        if (preds.length > 0) setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const handleSelect = async (p) => {
    justSelectedRef.current = true;
    setOpen(false);
    setQuery(p.main_text);
    try {
      const res = await apiClient.get(`/maps/places/details?place_id=${p.place_id}`);
      const d = res.data?.result;
      onChange({ name: d?.name || p.main_text, place_id: p.place_id, address: d?.address || p.secondary_text || "" });
    } catch {
      onChange({ name: p.main_text, place_id: p.place_id, address: p.secondary_text || "" });
    }
  };

  return (
    <div className="relative space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="relative">
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); if (!e.target.value) onChange(null); }}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder={placeholder || "Search airport…"}
          autoComplete="off"
          data-testid={testId}
        />
        {loading && <Loader2 className="absolute right-3 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />}
        {value?.name && !loading && (
          <button type="button" onClick={() => { onChange(null); setQuery(""); }}
            className="absolute right-3 top-2.5 text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 left-0 right-0 top-full mt-1 border rounded-lg bg-background shadow-lg max-h-48 overflow-y-auto">
          {results.map((p, i) => (
            <button key={`${p.place_id}-${i}`} type="button"
              onPointerDown={(e) => { e.preventDefault(); handleSelect(p); }}
              className="w-full text-left px-3 py-2 hover:bg-secondary text-sm">
              <span className="font-medium">{p.main_text}</span>
              <span className="text-muted-foreground ml-1 text-xs">— {p.secondary_text}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export const GigManager = ({ mode = "list" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  // Two-column form layout kicks in at Tailwind's lg (1024px); below that the
  // Gig Details columns are a single stack, so their card-style box is dropped
  // to reclaim width (see the column wrappers below).
  const isWideForm = useIsDesktop(1024);
  // Template chips: name + 3 action icons (dates/rename/delete) don't all
  // fit on one row on narrow phones — stack name above actions instead of
  // letting the row overflow and clip the last icon off-screen.
  const isDesktopChips = useIsDesktop(640);
  // The mobile bottom nav (md:hidden, i.e. shown below 768px) is fixed at the
  // bottom over z-9999 — the selection action bar has to clear it or it lands
  // hidden behind the nav and its Delete/Duplicate buttons can't be tapped.
  const isDesktopNav = useIsDesktop(768);
  const [gigs, setGigs] = useState([]);
  const [pastGigs, setPastGigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mileageInput, setMileageInput] = useState("");
  // Which leg of the day's travel this mileage covers. Round trip doubles the
  // miles; the single-leg options (×1) let multi-gig days log each leg without
  // double-counting the home journeys.
  const [mileagePhase, setMileagePhase] = useState("round_trip");
  const [mileageExpenseFormat, setMileageExpenseFormat] = useState("amount");
  // Regular non-gig destinations from Settings → Travel (e.g. a train station).
  const [regularPlaces, setRegularPlaces] = useState([]);
  const [mileageLogged, setMileageLogged] = useState(false);
  const [mileageLogging, setMileageLogging] = useState(false);
  // Mileage claims already logged for this gig (keyed by phase) — shown in the
  // calculator so you can see/replace each leg.
  const [gigMileageClaims, setGigMileageClaims] = useState([]);
  // Skip the very first phase-effect run so it doesn't clobber fetchGig's seed.
  const mileagePhaseInitRef = useRef(true);
  const [addPerDiemsToInvoice, setAddPerDiemsToInvoice] = useState(false);
  const [addMileageToInvoice, setAddMileageToInvoice] = useState(false);
  // "Add mileage to invoice" now matches "Log mileage claim"'s button +
  // locked-confirmation shape instead of being a plain checkbox.
  const [mileageInvoiceConfirmed, setMileageInvoiceConfirmed] = useState(false);
  const [activeTab, setActiveTab] = useState("upcoming");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterVenue, setFilterVenue] = useState("all");
  const [filterArtist, setFilterArtist] = useState("all");
  const [filterVisibility, setFilterVisibility] = useState("all");
  const [filterPaymentStatus, setFilterPaymentStatus] = useState("all");
  const [sortOrder, setSortOrder] = useState("asc");
  // Past tab defaults to most-recent-first, independent of the Upcoming tab's sort.
  const [pastSortOrder, setPastSortOrder] = useState("desc");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  // Duplicate / template dialog: which gig is the source of the copy?
  const [duplicateSource, setDuplicateSource] = useState(null);
  // Share dialog for a gig: { gig, kind: "family" | "dep" }
  const [shareDialog, setShareDialog] = useState(null);
  // "Add multiple dates from template" dialog: which saved template is the source.
  const [templateDatesSource, setTemplateDatesSource] = useState(null);
  const [gigToDelete, setGigToDelete] = useState(null);
  // Every GigCard shows a checkbox overlay; a floating action bar
  // (Add to Invoice / Duplicate / Create Template / Delete) appears once >=1 is picked.
  const [selectedGigIds, setSelectedGigIds] = useState(new Set());
  // Saved templates (reusable gig presets). Managed in a section on the Gigs
  // page; chosen from the Add Gig dropdown's template picker.
  const [templates, setTemplates] = useState([]);
  // "Name this template" dialog — holds the source gig while naming.
  const [templateNameDialog, setTemplateNameDialog] = useState(null); // { gig, name }
  const [savingTemplate, setSavingTemplate] = useState(false);
  // Template picker dialog (opened from the Add Gig dropdown)
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [bundling, setBundling] = useState(false);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [enrichQuery, setEnrichQuery] = useState("");
  const [collaborators, setCollaborators] = useState([]);
  const [bands, setBands] = useState([]);
  const [showFirstGigSuccess, setShowFirstGigSuccess] = useState(false);
  const [showFiltersSheet, setShowFiltersSheet] = useState(false);
  const [highlightGigId, setHighlightGigId] = useState(null);

  // Draggable drop-down blocks (Money / Tickets / Setlist / Notes) below The
  // Essentials. Order + open state persist per-user in localStorage; default is
  // the canonical order, all collapsed.
  const [pillOrder, setPillOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(GIG_PILL_ORDER_KEY));
      if (Array.isArray(saved)) {
        const known = saved.filter((pid) => DEFAULT_PILL_ORDER.includes(pid));
        const missing = DEFAULT_PILL_ORDER.filter((pid) => !known.includes(pid));
        return [...known, ...missing];
      }
    } catch {}
    return DEFAULT_PILL_ORDER;
  });
  const [openPills, setOpenPills] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(GIG_PILL_OPEN_KEY));
      if (saved && typeof saved === "object") return saved;
    } catch {}
    return {}; // all collapsed by default
  });
  useEffect(() => {
    try { localStorage.setItem(GIG_PILL_ORDER_KEY, JSON.stringify(pillOrder)); } catch {}
  }, [pillOrder]);
  useEffect(() => {
    try { localStorage.setItem(GIG_PILL_OPEN_KEY, JSON.stringify(openPills)); } catch {}
  }, [openPills]);
  const pillSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const handlePillDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setPillOrder((order) => {
      const oldIndex = order.indexOf(active.id);
      const newIndex = order.indexOf(over.id);
      if (oldIndex < 0 || newIndex < 0) return order;
      return arrayMove(order, oldIndex, newIndex);
    });
  };
  const togglePill = (pid) => setOpenPills((o) => ({ ...o, [pid]: !o[pid] }));

  // Scroll to and highlight a gig card when navigated from Calendar
  useEffect(() => {
    const targetId = location.state?.highlightGigId;
    if (!targetId || loading) return;
    const isUpcoming = gigs.some(g => g.gig_id === targetId);
    const isPast = !isUpcoming && pastGigs.some(g => g.gig_id === targetId);
    if (!isUpcoming && !isPast) return;
    if (isPast) setActiveTab('past');
    setHighlightGigId(targetId);
    window.history.replaceState({}, document.title);
    setTimeout(() => {
      document.getElementById(`gig-card-${targetId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, isPast ? 50 : 0);
    setTimeout(() => setHighlightGigId(null), 2000);
  }, [gigs, pastGigs, loading]);

  // Watch for firstGig state from navigation (for first gig success overlay)
  useEffect(() => {
    if (location.state?.firstGig && mode === "edit" && id) {
      setShowFirstGigSuccess(true);
      // Clear the state so it doesn't persist on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.firstGig, mode, id]);

  // Form state for create/edit
  const [formData, setFormData] = useState(EMPTY_FORM);

  // Pre-fill the miles box with the estimate for the chosen journey phase:
  // venue legs use the home→venue estimate, airport legs use the home→airport
  // estimate, "between venues" has no estimate so it's left for manual entry.
  useEffect(() => {
    if (mileagePhaseInitRef.current) { mileagePhaseInitRef.current = false; return; }
    setMileageInput(mileagePhaseDistance(mileagePhase, formData, regularPlaces));
    setMileageLogged(false);
  }, [mileagePhase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Travel settings load separately from the gig, so a saved regular-place leg
  // can't be seeded until the places arrive — fill the box then if it's empty.
  useEffect(() => {
    if (!parsePlacePhase(mileagePhase) || mileageInput) return;
    const seed = mileagePhaseDistance(mileagePhase, formData, regularPlaces);
    if (seed) setMileageInput(seed);
  }, [regularPlaces, mileagePhase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset all form state when entering create mode so that fields never carry
  // over from a previous submission.  React reuses this component instance
  // across /gigs, /gigs/new and /gigs/:id/edit (same component type, same
  // tree position), so useState initial values are not re-applied on route
  // change — we have to do it explicitly.
  useEffect(() => {
    if (mode === "create") {
      setFormData(EMPTY_FORM);
      setEnrichQuery("");
      setVenueQuery("");
      setVenueResults([]);
      setPlacesResults([]);
      setShowVenueDropdown(false);
      setShowAddVenueForm(false);
      setAddVenueData({ name: "", city: "", postcode: "", country: "UK", capacity: "", website: "" });
      setSmartFillVenueCandidates([]);
      setCollabSearchQuery("");
    }
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Collaborator search state
  const [collabSearchQuery, setCollabSearchQuery] = useState("");
  const [collabQuickAdd, setCollabQuickAdd] = useState(false);
  const [collabAdding, setCollabAdding] = useState(false);
  // Email input for contacts without email (collaborator_id -> email)
  const [collabEmailInputs, setCollabEmailInputs] = useState({});

  // Venue autocomplete state
  const [venueQuery, setVenueQuery] = useState("");
  const [venueResults, setVenueResults] = useState([]);
  const [placesResults, setPlacesResults] = useState([]);
  const [venueSearching, setVenueSearching] = useState(false);
  const [showVenueDropdown, setShowVenueDropdown] = useState(false);
  const [showAddVenueForm, setShowAddVenueForm] = useState(false);
  const [addVenueData, setAddVenueData] = useState({ name: "", city: "", postcode: "", country: "UK", capacity: "", website: "" });
  const [addVenueSaving, setAddVenueSaving] = useState(false);
  const [addVenueDuplicateWarning, setAddVenueDuplicateWarning] = useState(null);

  // Invoice recipient live preview (shown below "Invoice to" picker in the form)
  const [recipientPreview, setRecipientPreview] = useState(null);

  // Share state
  const [shareEnabled, setShareEnabled] = useState(false);
  const [shareToken, setShareToken] = useState(null);
  const [shareToggling, setShareToggling] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  // Smart Fill venue-picker state — populated by /gigs/parse-schedule response.
  // When non-empty, an inline picker renders below the Smart Fill input so
  // the user can confirm which Google-Places venue (if any) matches.
  const [smartFillVenueCandidates, setSmartFillVenueCandidates] = useState([]);
  const [smartFillVenueSaving, setSmartFillVenueSaving] = useState(false);

  // Multi-date Smart Fill — when a paste contains more than one dated event,
  // /gigs/parse-schedule returns them all and this holds the preview until the
  // user confirms. Nothing is written until they do.
  const [scheduleParsed, setScheduleParsed] = useState(null);
  const [scheduleReviewOpen, setScheduleReviewOpen] = useState(false);

  // Artist smart search state
  const [showArtistDropdown, setShowArtistDropdown] = useState(false);
  const [artistQuickAdd, setArtistQuickAdd] = useState(false); // show quick-add buttons
  const [artistAdding, setArtistAdding] = useState(false);

  useEffect(() => {
    const name = formData.artist_name.trim();
    if (
      name.length > 1 &&
      !bands.some(b => b.name.toLowerCase() === name.toLowerCase()) &&
      !collaborators.some(c => c.name.toLowerCase() === name.toLowerCase())
    ) {
      setArtistQuickAdd(true);
    } else {
      setArtistQuickAdd(false);
    }
  }, [formData.artist_name, bands, collaborators]);

  useEffect(() => {
    const q = collabSearchQuery.trim().toLowerCase();
    if (
      q.length >= 2 &&
      collaborators.filter(c =>
        c.name.toLowerCase().includes(q) &&
        !(formData.collaborator_ids || []).includes(c.collaborator_id)
      ).length === 0
    ) {
      setCollabQuickAdd(true);
    } else {
      setCollabQuickAdd(false);
    }
  }, [collabSearchQuery, collaborators, formData.collaborator_ids]);

  useEffect(() => {
    // Fetch mileage format preference whenever we're in a gig form
    if (mode === "edit" || mode === "create") {
      apiClient.get("/user/travel-settings")
        .then(r => {
          setMileageExpenseFormat(r.data.mileage_expense_format || "amount");
          setRegularPlaces(r.data.regular_places || []);
        })
        .catch(() => {
          // Without this, regular places silently disappear from the mileage calculator
          toast.error("Couldn't load your travel settings, so regular places are missing. Refresh to try again.");
        });
    }
  }, [mode]);

  useEffect(() => {
    if (mode === "list") {
      fetchGigs();
      fetchTemplates();
    } else if (mode === "edit" && id) {
      fetchGig(id);
      fetchCollaborators();
      fetchBands();
    } else if (mode === "create") {
      fetchCollaborators();
      fetchBands();
      setLoading(false);
    } else {
      setLoading(false);
    }
    // Refresh on one-click FA/Xero push so the gig's fee_status flips
    // from "pending" → "invoiced" and the FA/X buttons disappear.
    const onInvoiced = () => { if (mode === "list") fetchGigs(); };
    window.addEventListener("fretless:gig-invoiced", onInvoiced);
    return () => window.removeEventListener("fretless:gig-invoiced", onInvoiced);
  }, [mode, id]);

  // Prefill the create form from a saved template (?template=<id>). The picker
  // passes the template object via router state; on a hard refresh we fall back
  // to fetching the list and finding it by id. Date is always left blank.
  useEffect(() => {
    if (mode !== "create") return;
    const templateId = searchParams.get("template");
    if (!templateId) return;
    const fromState = location.state?.template;
    if (fromState?.template_id === templateId) {
      applyTemplateToForm(fromState);
      return;
    }
    (async () => {
      try {
        const res = await apiClient.get("/gig-templates");
        const t = (res.data || []).find((x) => x.template_id === templateId);
        if (t) applyTemplateToForm(t);
      } catch (err) {
        console.error("Failed to load template:", err);
      }
    })();
  }, [mode, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Prefill the create form's date from ?date=YYYY-MM-DD — e.g. "Add Gig"
  // tapped from a specific day in Calendar view — so the date the user
  // already picked by navigating there doesn't have to be re-entered.
  // formData.date is a Date object everywhere else (the date-picker's
  // onSelect sets it that way), so parse the string rather than storing it
  // raw — a bare string here would crash the "Pick a date" button's
  // format(formData.date, "PPP") call.
  useEffect(() => {
    if (mode !== "create") return;
    const dateParam = searchParams.get("date");
    if (!dateParam) return;
    try {
      setFormData((prev) => ({ ...prev, date: parseISO(dateParam) }));
    } catch {}
  }, [mode, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open the template picker when navigated here with
  // ?openTemplatePicker=true (Calendar's "Create from template" option) — the
  // accompanying ?date= rides along and startGigFromTemplate/applyTemplateToForm
  // pick it up once a template is actually chosen.
  useEffect(() => {
    if (mode !== "list") return;
    if (searchParams.get("openTemplatePicker") === "true") {
      setShowTemplatePicker(true);
    }
  }, [mode, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced live preview: whenever fields that affect recipient resolution change,
  // ask the backend who the invoice would go to (without saving the gig).
  useEffect(() => {
    if (mode !== "create" && mode !== "edit") return;
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (formData.venue_id) params.set("venue_id", formData.venue_id);
        if (formData.band_id) params.set("band_id", formData.band_id);
        if (formData.venue_name) params.set("venue_name", formData.venue_name);
        if (formData.artist_name) params.set("artist_name", formData.artist_name);
        if (formData.invoice_recipient_type && formData.invoice_recipient_type !== "auto")
          params.set("invoice_recipient_type", formData.invoice_recipient_type);
        if (formData.invoice_recipient_id) params.set("invoice_recipient_id", formData.invoice_recipient_id);
        if (formData.invoice_recipient_name) params.set("invoice_recipient_name", formData.invoice_recipient_name);
        if (formData.invoice_recipient_email) params.set("invoice_recipient_email", formData.invoice_recipient_email);
        const res = await apiClient.get(`/gigs/resolve-recipient-preview?${params}`);
        setRecipientPreview(res.data);
      } catch {
        setRecipientPreview(null);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [
    formData.venue_id, formData.band_id, formData.venue_name, formData.artist_name,
    formData.invoice_recipient_type, formData.invoice_recipient_id,
    formData.invoice_recipient_name, formData.invoice_recipient_email,
    mode,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCollaborators = async () => {
    try {
      const response = await apiClient.get("/collaborators");
      setCollaborators(response.data || []);
    } catch (error) {
      console.error("Failed to fetch collaborators:", error);
    }
  };

  const fetchBands = async () => {
    try {
      const response = await apiClient.get("/bands");
      setBands(response.data || []);
    } catch (error) {
      console.error("Failed to fetch bands:", error);
    }
  };

  const addCollaboratorToBand = async (collaboratorId) => {
    if (!formData.band_id) return;
    try {
      await apiClient.post(`/bands/${formData.band_id}/members`, { collaborator_id: collaboratorId });
    } catch (error) {
      console.error("Failed to add collaborator to band:", error);
    }
  };

  // Put a band member on the gig (used for social tags). Inviting is separate, and
  // needs an email, so it is never done here. Duplicate-guarded for "Add all".
  const addBandMemberToGig = (m) => {
    setFormData(prev => {
      const ids = prev.collaborator_ids || [];
      if (ids.includes(m.collaborator_id)) return prev;
      return { ...prev, collaborator_ids: [...ids, m.collaborator_id] };
    });
  };

  // Venue autocomplete search (local DB + Google Places) with 300ms debounce
  useEffect(() => {
    if (venueQuery.length < 2) {
      setVenueResults([]);
      setPlacesResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setVenueSearching(true);
      try {
        const res = await apiClient.get(`/maps/places/autocomplete?q=${encodeURIComponent(venueQuery)}`);
        setVenueResults(res.data?.local_venues || []);
        setPlacesResults(res.data?.predictions || []);
        setShowVenueDropdown(true);
      } catch (err) {
        console.error("Venue autocomplete error:", err);
        setVenueResults([]);
        setPlacesResults([]);
      } finally {
        setVenueSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [venueQuery]);

  const handleOpenAddVenueForm = () => {
    setShowVenueDropdown(false);
    setAddVenueData({ name: formData.venue_name || venueQuery, city: formData.city || "", postcode: "", country: formData.country || "UK", capacity: "", website: "" });
    setAddVenueDuplicateWarning(null);
    setShowAddVenueForm(true);
  };

  const handleSelectPlace = async (place) => {
    setShowVenueDropdown(false);
    setVenueQuery("");
    try {
      const res = await apiClient.get(`/maps/places/details?place_id=${place.place_id}`);
      const d = res.data?.result;
      if (d) {
        // Save/update venue in shared_venues with place_id — response gives us the canonical venue_id.
        let venueId = null;
        try {
          const venueRes = await apiClient.post("/venues", {
            name: d.name || place.main_text,
            city: d.city || "",
            country: d.country || "UK",
            postcode: d.postcode || "",
            website: d.website || "",
            google_maps_link: d.google_maps_link || "",
            place_id: place.place_id,
          });
          venueId = venueRes.data?.venue_id || null;
        } catch {} // Silent — venue may already exist
        
        setFormData(prev => ({
          ...prev,
          venue_name: d.name || place.main_text,
          venue_id: venueId || prev.venue_id,
          venue_address: d.address || "",
          city: d.city || prev.city,
          country: d.country || prev.country,
          venue_website: d.website || prev.venue_website,
        }));
        
        toast.success(`Selected ${d.name || place.main_text}`);
      }
    } catch {
      setFormData(prev => ({ ...prev, venue_name: place.main_text }));
    }
  };

  const handleAddVenueSubmit = async () => {
    if (!addVenueData.name.trim() || !addVenueData.city.trim()) {
      toast.error("Venue name and city are required");
      return;
    }
    setAddVenueSaving(true);
    setAddVenueDuplicateWarning(null);
    try {
      // Check for duplicate by name + postcode first (if postcode provided)
      if (addVenueData.postcode.trim()) {
        const searchRes = await apiClient.get(`/venues/search?q=${encodeURIComponent(addVenueData.name)}&limit=20`);
        const duplicate = (searchRes.data || []).find(v =>
          v.name.toLowerCase() === addVenueData.name.trim().toLowerCase() &&
          (v.postcode || "").toLowerCase() === addVenueData.postcode.trim().toLowerCase()
        );
        if (duplicate) {
          setAddVenueDuplicateWarning(duplicate);
          setAddVenueSaving(false);
          return;
        }
      }
      const payload = {
        name: addVenueData.name.trim(),
        city: addVenueData.city.trim(),
        country: addVenueData.country.trim() || "UK",
        postcode: addVenueData.postcode.trim() || undefined,
        capacity: addVenueData.capacity ? parseInt(addVenueData.capacity) : undefined,
        website: addVenueData.website.trim() || undefined,
      };
      const res = await apiClient.post("/venues", payload);
      const newVenue = res.data;
      setFormData(prev => ({
        ...prev,
        venue_name: newVenue.name,
        venue_id: newVenue.venue_id || prev.venue_id,
        city: newVenue.city || prev.city,
        country: newVenue.country || prev.country,
        venue_website: newVenue.website || prev.venue_website,
      }));
      setShowAddVenueForm(false);
      setVenueQuery("");
      toast.success(`Venue "${newVenue.name}" added and selected`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add venue");
    } finally {
      setAddVenueSaving(false);
    }
  };

  const handleUseExistingVenue = (v) => {
    setFormData(prev => ({
      ...prev,
      venue_name: v.name,
      venue_id: v.venue_id || prev.venue_id,
      city: v.city || prev.city,
      country: v.country || prev.country,
      venue_website: v.website || prev.venue_website,
    }));
    setShowAddVenueForm(false);
    setAddVenueDuplicateWarning(null);
    setVenueQuery("");
    toast.success(`Selected existing venue: ${v.name}`);
  };

  // ───── Saved templates ──────────────────────────────────────────────
  const fetchTemplates = async () => {
    try {
      const res = await apiClient.get("/gig-templates");
      setTemplates(res.data || []);
    } catch (err) {
      console.error("Failed to fetch templates:", err);
    }
  };

  // Prefill the create form from a template: reuse every saved field.
  // Date is blank unless a ?date= was carried along (e.g. "Create from
  // template" tapped from a specific day in Calendar view) — read directly
  // from the URL rather than relying on the separate date-prefill effect's
  // ordering, since this can also run from an async template-fetch fallback
  // that would otherwise race it.
  const applyTemplateToForm = (template) => {
    if (!template?.fields) return;
    const dateParam = searchParams.get("date");
    let prefillDate = "";
    if (dateParam) {
      try { prefillDate = parseISO(dateParam); } catch {}
    }
    setFormData({ ...EMPTY_FORM, ...template.fields, date: prefillDate });
  };

  const handleCreateTemplate = async () => {
    if (!templateNameDialog?.gig) return;
    const name = (templateNameDialog.name || "").trim();
    if (!name) { toast.error("Give the template a name"); return; }
    setSavingTemplate(true);
    try {
      await apiClient.post("/gig-templates", { gig_id: templateNameDialog.gig.gig_id, name });
      toast.success(`Saved template "${name}"`);
      setTemplateNameDialog(null);
      setSelectedGigIds(new Set());
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      await apiClient.delete(`/gig-templates/${templateId}`);
      setTemplates((prev) => prev.filter((t) => t.template_id !== templateId));
      toast.success("Template deleted");
    } catch (err) {
      toast.error("Failed to delete template");
    }
  };

  const handleRenameTemplate = async (templateId, currentName) => {
    const name = window.prompt("Rename template", currentName);
    if (name == null) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === currentName) return;
    try {
      await apiClient.patch(`/gig-templates/${templateId}`, { name: trimmed });
      setTemplates((prev) => prev.map((t) => (t.template_id === templateId ? { ...t, name: trimmed } : t)));
      toast.success("Renamed");
    } catch (err) {
      toast.error("Failed to rename template");
    }
  };

  // Choosing a template from the picker → create form prefilled (date blank,
  // unless a ?date= was carried along from wherever the picker was opened).
  const startGigFromTemplate = (template) => {
    setShowTemplatePicker(false);
    const dateParam = searchParams.get("date");
    const dateQuery = dateParam ? `&date=${dateParam}` : "";
    navigate(`/gigs/new?template=${template.template_id}${dateQuery}`, { state: { template } });
  };

  const fetchGigs = async () => {
    try {
      // Fetch upcoming and past gigs in parallel
      const [upcomingRes, pastRes] = await Promise.all([
        apiClient.get("/gigs?time_filter=upcoming"),
        apiClient.get("/gigs?time_filter=past")
      ]);
      setGigs(upcomingRes.data);
      setPastGigs(pastRes.data);
    } catch (error) {
      toast.error("Failed to fetch gigs");
    } finally {
      setLoading(false);
    }
  };

  const fetchGig = async (gigId) => {
    try {
      const response = await apiClient.get(`/gigs/${gigId}`);
      const gig = response.data;

      // Safely parse dates - gigLocalDate returns YYYY-MM-DD or null
      const parseLocalDate = (dateStr) => {
        if (!dateStr) return null;
        const localDateStr = gigLocalDate(dateStr);
        if (!localDateStr) return null;
        return new Date(localDateStr + 'T12:00:00');
      };

      // Convert backend per_diems (flat totals structure) to frontend format (nested value/quantity)
      // Backend stores only totals, so we put the total in value and default quantity to 1
      const convertPerDiems = (pd) => {
        if (!pd) return { breakfast: { value: "", quantity: "" }, lunch: { value: "", quantity: "" }, dinner: { value: "", quantity: "" } };
        return {
          breakfast: pd.breakfast ? { value: String(pd.breakfast), quantity: "1" } : { value: "", quantity: "" },
          lunch: pd.lunch ? { value: String(pd.lunch), quantity: "1" } : { value: "", quantity: "" },
          dinner: pd.dinner ? { value: String(pd.dinner), quantity: "1" } : { value: "", quantity: "" },
        };
      };

      setFormData({
        ...gig,
        date: parseLocalDate(gig.date),
        end_date: parseLocalDate(gig.end_date),
        invitations: gig.invitations || [],
        per_diems: convertPerDiems(gig.per_diems),
      });
      setAddPerDiemsToInvoice(gig.add_per_diems_to_invoice || false);
      setAddMileageToInvoice(gig.add_mileage_to_invoice || false);
      // Restore the saved mileage leg so a fly-out's "home ↔ airport" choice
      // persists. Fall back to the airport leg for fly-out gigs (multi-day with a
      // departure airport), else the home↔venue leg. Seed the box from the
      // distance for whichever leg we land on — not always the venue distance.
      const savedPhase = gig.mileage_phase
        || ((gig.is_multiday && (gig.departure_airport?.name || gig.departure_airport?.place_id))
            ? "airport_round_trip" : "round_trip");
      setMileagePhase(savedPhase);
      const seedMiles = mileagePhaseDistance(savedPhase, gig, regularPlaces);
      if (seedMiles) {
        setMileageInput(seedMiles);
      } else if (savedPhase !== "between" && !parsePlacePhase(savedPhase) && gig.estimated_distance_miles > 0) {
        setMileageInput(String(gig.estimated_distance_miles));
      }
      // Load any mileage already logged for this gig (per phase)
      try {
        const mc = await apiClient.get(`/mileage-claims?gig_id=${gigId}`);
        setGigMileageClaims(mc.data || []);
      } catch { /* non-fatal */ }
      setShareEnabled(gig.share_enabled || false);
      setShareToken(gig.share_token || null);
    } catch (error) {
      toast.error("Failed to fetch gig");
      navigate("/gigs");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.artist_name || !formData.venue_name || !formData.city || !formData.date) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      // Clean up the payload - convert empty strings to null
      const payload = {
        artist_name: formData.artist_name,
        venue_name: formData.venue_name,
        venue_id: formData.venue_id || null,
        city: formData.city,
        country: formData.country || "UK",
        date: formData.date instanceof Date ? format(formData.date, "yyyy-MM-dd") : formData.date,
        end_date: formData.end_date instanceof Date ? format(formData.end_date, "yyyy-MM-dd") : (formData.end_date || null),
        start_time: formData.start_time || null,
        end_time: formData.end_time || null,
        ticket_link: formData.ticket_link || null,
        ticket_price: formData.ticket_price || null,
        venue_website: formData.venue_website || null,
        venue_address: formData.venue_address || null,
        status: formData.status || "confirmed",
        genre: formData.genre || "jazz",
        fee: formData.fee ? parseFloat(formData.fee) : null,
        expenses: formData.expenses ? parseFloat(formData.expenses) : null,
        fee_currency: formData.fee_currency || "GBP",
        fee_status: formData.fee_status || "pending",
        is_recurring: formData.is_recurring || false,
        recurrence_pattern: formData.recurrence_pattern || null,
        recurrence_day: formData.recurrence_day ? parseInt(formData.recurrence_day) : null,
        recurrence_end_date: formData.recurrence_end_date || null,
        // Missing fields - now included
        call_time: formData.call_time || null,
        dress_code: formData.dress_code || null,
        gig_notes: formData.gig_notes || null,
        setlist: formData.setlist || [],
        schedule: formData.schedule || [],
        accommodation: formData.accommodation?.name || formData.accommodation?.maps_link || formData.accommodation?.distance_from_venue || formData.accommodation?.notes ? formData.accommodation : null,
        per_diems: (formData.per_diems?.breakfast?.value || formData.per_diems?.breakfast?.quantity || formData.per_diems?.lunch?.value || formData.per_diems?.lunch?.quantity || formData.per_diems?.dinner?.value || formData.per_diems?.dinner?.quantity) ? {
          breakfast: formData.per_diems?.breakfast?.value ? parseFloat(formData.per_diems.breakfast.value) * (parseInt(formData.per_diems.breakfast.quantity) || 1) : null,
          lunch: formData.per_diems?.lunch?.value ? parseFloat(formData.per_diems.lunch.value) * (parseInt(formData.per_diems.lunch.quantity) || 1) : null,
          dinner: formData.per_diems?.dinner?.value ? parseFloat(formData.per_diems.dinner.value) * (parseInt(formData.per_diems.dinner.quantity) || 1) : null,
        } : null,
        add_per_diems_to_invoice: addPerDiemsToInvoice,
        add_mileage_to_invoice: addMileageToInvoice,
        // estimated_distance_miles is intentionally NOT sent here — it's a
        // server-computed estimate (estimate_travel_for_gig, refreshed
        // whenever venue/city fields change) that seeds the mileage
        // calculator on load. Round-tripping the calculator's local input
        // back through this endpoint used to let a stale client-side figure
        // clobber a freshly recomputed distance on every save.
        // Persist the actual mile count alongside the £ cost so invoice line
        // items can show "27 mi @ £0.45/mi" instead of a flat lump sum.
        mileage_miles_for_invoice: (() => {
          if (!addMileageToInvoice) return null;
          const onewayMiles = parseFloat(mileageInput) || 0;
          const totalMiles = parseFloat((onewayMiles * mileagePhaseMultiplier(mileagePhase)).toFixed(1));
          return totalMiles > 0 ? totalMiles : null;
        })(),
        mileage_cost_for_invoice: (() => {
          if (!addMileageToInvoice) return null;
          const onewayMiles = parseFloat(mileageInput) || 0;
          const totalMiles = onewayMiles * mileagePhaseMultiplier(mileagePhase);
          return totalMiles > 0 ? parseFloat((totalMiles * HMRC_MILEAGE_RATE).toFixed(2)) : null;
        })(),
        // Persist the selected mileage leg so a fly-out gig's "home ↔ airport"
        // choice survives save/reopen (otherwise it reset to the venue leg).
        mileage_phase: mileagePhase,
        is_multiday: formData.is_multiday || false,
        travel_date: formData.travel_date || null,
        departure_time: formData.departure_time || null,
        return_date: formData.return_date || null,
        return_time: formData.return_time || null,
        airport_buffer: formData.airport_buffer ? parseInt(formData.airport_buffer) : null,
        departure_airport: formData.departure_airport?.name ? formData.departure_airport : null,
        destination_airport: formData.destination_airport?.name ? formData.destination_airport : null,
        return_airport: formData.return_airport?.name ? formData.return_airport : null,
        is_public: formData.is_public !== false,
        gig_type: formData.gig_type || "public",
        tour_id: formData.tour_id || null,
        // Transport
        transport_type: formData.transport_type || null,
        transport_notes: formData.transport_notes || null,
        // Band & Performers
        band_id: formData.band_id || null,
        performing_member_ids: formData.performing_member_ids || [],
        collaborator_ids: formData.collaborator_ids || [],
        // Per-gig invoice recipient override
        invoice_recipient_type: formData.invoice_recipient_type || "auto",
        invoice_recipient_id: formData.invoice_recipient_id || null,
        invoice_recipient_name: formData.invoice_recipient_name || null,
        invoice_recipient_email: formData.invoice_recipient_email || null,
        // Invitations (availability RSVP)
        invitations: (formData.invitations || []).filter(i => i.status !== "pending" || (i.email || "").trim())
      };

      if (mode === "edit" && id) {
        await apiClient.put(`/gigs/${id}`, payload);
        toast.success("Gig updated successfully");
        navigate("/gigs");
      } else {
        const response = await apiClient.post("/gigs", payload);
        toast.success("Gig created successfully");
        if (response.data.is_first_gig) {
          // Redirect to the gig edit page with first-gig flag
          navigate(`/gigs/${response.data.gig_id}/edit`, { state: { firstGig: true } });
        } else {
          navigate("/gigs");
        }
      }
    } catch (error) {
      console.error("Gig save error:", error.response?.data);
      if (error.response?.status === 403) {
        toast.error(error.response.data?.detail || "Free plan limit reached. Start your trial for unlimited gigs.");
      } else {
        toast.error(mode === "edit" ? "Failed to update gig" : "Failed to create gig");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Share toggle handler
  const handleShareToggle = async () => {
    if (!id) return;
    setShareToggling(true);
    try {
      const res = await apiClient.put(`/gigs/${id}/share`);
      setShareEnabled(res.data.share_enabled);
      setShareToken(res.data.share_token);
      toast.success(res.data.share_enabled ? "Sharing enabled" : "Sharing disabled");
    } catch {
      toast.error("Failed to toggle sharing");
    } finally {
      setShareToggling(false);
    }
  };

  // Share URL points to OG tag endpoint for rich previews in iMessage/WhatsApp.
  // Must be absolute against the backend (Railway) — a relative `/api/...` resolves
  // to the frontend domain (www.fretless.life), which has no /api. See ops-playbook #1.
  const apiBase = API.startsWith("http") ? API : window.location.origin + API;
  const shareUrl = shareToken ? `${apiBase}/share/gig/${shareToken}/og` : null;

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    await copyToClipboard(shareUrl);
    setLinkCopied(true);
    toast.success("Link copied!");
    setTimeout(() => setLinkCopied(false), 2000);
  };

  // Smart Fill - search web, paste text, or voice input to auto-populate gig details
  const handleSmartFill = async () => {
    const query = enrichQuery.trim();
    
    if (!query || query.length < 3) {
      toast.error("Enter a URL, search keywords, or paste booking text");
      return;
    }

    setEnriching(true);
    try {
      const isUrl = query.startsWith('http://') || query.startsWith('https://');

      // Append extracted notes/description instead of replacing — gig_notes
      // often holds the user's own writing. Re-running Smart Fill with the
      // same source is a no-op (skip when the text is already present).
      const mergeNotes = (existing, found) => {
        if (!found) return existing || "";
        if (!existing) return found;
        return existing.includes(found) ? existing : `${existing}\n${found}`;
      };

      if (isUrl) {
        // URL mode — enhanced with Places API fallback.
        // Note: `fee` is intentionally NOT mapped here. Any monetary value
        // surfaced by /gigs/enrich-url is a public ticket/entry price (handled
        // via `ticket_price`), never the performer's fee. Keep this branch
        // free of `fee` to avoid polluting that field with audience prices.
        const response = await apiClient.post("/gigs/enrich-url", { url: query });
        const data = response.data;
        
        if (data.success && data.found_data) {
          const fd = data.found_data;
          // Found values override what's already in the form — running Smart
          // Fill on a populated gig is an explicit "re-fill from this source"
          // action. Existing values survive only for fields the scrape came
          // back empty on.
          setFormData(prev => ({
            ...prev,
            artist_name: fd.event_title || prev.artist_name || "",
            venue_name: fd.venue_name || prev.venue_name || "",
            // A replaced venue name must drop the old venue_id — keeping it
            // would link the gig (stats, invite emails) to the wrong venue.
            venue_id: fd.venue_name && fd.venue_name !== prev.venue_name ? null : prev.venue_id,
            venue_address: fd.venue_address || prev.venue_address || "",
            venue_website: fd.venue_website || prev.venue_website || "",
            city: fd.city || prev.city || "",
            ticket_link: fd.ticket_link || prev.ticket_link || "",
            ticket_price: fd.ticket_price || prev.ticket_price || "",
            start_time: fd.start_time || prev.start_time || "",
            // Scraped event description (og/meta, capped at 500 chars
            // server-side) lands in notes — the tester surfaces it, so the
            // gig form should too.
            gig_notes: mergeNotes(prev.gig_notes, fd.description),
            date: fd.date ? new Date(fd.date + 'T12:00:00') : prev.date
          }));
          const found = [];
          if (fd.event_title || fd.venue_name) found.push("venue");
          if (fd.city) found.push("city");
          if (fd.date) found.push("date");
          if (fd.start_time) found.push("time");
          if (fd.ticket_price) found.push("price");
          if (fd.description) found.push("notes");
          toast.success(found.length ? `Found: ${found.join(", ")}` : "Scraped page — review fields below");

          // Sparse-response fallback — if the scraper only returned a
          // ticket_link and nothing else useful, derive an artist name from
          // the URL slug so the user has something to anchor on, and prompt
          // them to switch to paste-text mode for richer extraction.
          const isSparse = !fd.event_title && !fd.venue_name && !fd.date && !fd.start_time;
          if (isSparse) {
            try {
              const u = new URL(query);
              // Last non-empty path segment, with .html / .htm trimmed off.
              const segments = u.pathname.split("/").map(s => s.trim()).filter(Boolean);
              const lastSlug = (segments[segments.length - 1] || "").replace(/\.html?$/i, "");
              const slugName = lastSlug
                .replace(/[-_+]+/g, " ")
                .trim()
                .split(/\s+/)
                .map(w => w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : "")
                .join(" ");
              if (slugName) {
                setFormData(prev => ({
                  ...prev,
                  artist_name: prev.artist_name || slugName,
                }));
              }
            } catch {
              // Malformed URL — silently skip slug parsing.
            }
            toast("We couldn't read that page automatically — try copying the event text and pasting it into Smart Fill instead", { duration: 8000 });
          }
        } else {
          toast.error("Couldn't extract details from that URL. Try keywords or paste text instead.");
        }
      } else {
        // Text / paste / voice input — AI extraction via /gigs/parse-schedule.
        // Returns { shared, events[], total, duplicates, truncated }. Each event
        // carries the same fields the old /gigs/parse-text returned (title,
        // venue_name, city, date, start_time, finish_time, call_time, dress_code,
        // fee, notes, schedule, accommodation, per_diems, travel fields,
        // venue_candidates) plus gig_type and is_duplicate, with null for
        // anything not found. One event fills this form; several open the
        // review modal.
        const response = await apiClient.post("/gigs/parse-schedule", { text: query });
        const parsed = response.data || {};
        const parsedEvents = Array.isArray(parsed.events) ? parsed.events : [];

        // More than one dated event — hand off to the review modal rather than
        // collapsing a whole schedule into this single form.
        if (parsedEvents.length > 1) {
          setScheduleParsed(parsed);
          setScheduleReviewOpen(true);
          toast.success(`Found ${parsedEvents.length} events — review before creating`);
          return;
        }

        // One event (or none): behave exactly as before and fill this form.
        const ex = parsedEvents[0] || {};

        // Extracted values override what's already in the form (see URL mode
        // above) — existing values survive only for fields the AI returned
        // null on.
        setFormData(prev => ({
          ...prev,
          artist_name: ex.title || prev.artist_name || "",
          venue_name: ex.venue_name || prev.venue_name || "",
          // Same guard as URL mode: a replaced venue name drops the stale
          // venue_id. The venue picker below re-links it when a candidate
          // is chosen.
          venue_id: ex.venue_name && ex.venue_name !== prev.venue_name ? null : prev.venue_id,
          city: ex.city || prev.city || "",
          start_time: ex.start_time || prev.start_time || "",
          // AI returns the gig finish time as `finish_time`; the form field is
          // `end_time`. Map AI's finish_time onto the form's end_time so the
          // visible "End Time" input gets populated.
          end_time: ex.finish_time || prev.end_time || "",
          call_time: ex.call_time || prev.call_time || "",
          dress_code: ex.dress_code || prev.dress_code || "",
          // The parser now classifies rehearsal / function / session. Mirror the
          // gig-type Select, which also flips visibility.
          gig_type: ex.gig_type || prev.gig_type,
          is_public: ex.gig_type ? ex.gig_type === "public" : prev.is_public,
          fee: (ex.fee != null ? String(ex.fee).replace(/[^0-9.]/g, "") : "") || prev.fee || "",
          gig_notes: mergeNotes(prev.gig_notes, ex.notes),
          date: ex.date ? new Date(ex.date + 'T12:00:00') : prev.date,
          // Multi-day span (calendar days, NOT flights). The backend only
          // returns end_date when the text clearly spans >1 day and it's after
          // the start date; map it onto the form's end date picker.
          end_date: ex.end_date ? new Date(ex.end_date + 'T12:00:00') : prev.end_date,
          // Running order extracted by the AI (additive — separate from
          // start_time / call_time). Override only when the AI actually found
          // items; otherwise keep whatever's already in the form.
          schedule: Array.isArray(ex.schedule) && ex.schedule.length ? ex.schedule : prev.schedule,
          // Accommodation and per diems extracted by the AI. Override only when
          // the AI actually found them (at least one field populated); otherwise
          // keep existing form state.
          accommodation: (ex.accommodation && (ex.accommodation.name || ex.accommodation.maps_link || ex.accommodation.distance_from_venue || ex.accommodation.notes)) ? ex.accommodation : prev.accommodation,
          per_diems: (ex.per_diems && (ex.per_diems.breakfast || ex.per_diems.lunch || ex.per_diems.dinner)) ? {
            breakfast: ex.per_diems.breakfast?.value ? { value: String(ex.per_diems.breakfast.value), quantity: String(ex.per_diems.breakfast?.quantity || "") } : prev.per_diems?.breakfast || { value: "", quantity: "" },
            lunch: ex.per_diems.lunch?.value ? { value: String(ex.per_diems.lunch.value), quantity: String(ex.per_diems.lunch?.quantity || "") } : prev.per_diems?.lunch || { value: "", quantity: "" },
            dinner: ex.per_diems.dinner?.value ? { value: String(ex.per_diems.dinner.value), quantity: String(ex.per_diems.dinner?.quantity || "") } : prev.per_diems?.dinner || { value: "", quantity: "" },
          } : prev.per_diems,
          // Travel fields — override only when AI found flight info.
          is_multiday: ex.is_multiday ? true : prev.is_multiday,
          travel_date: ex.travel_date || prev.travel_date,
          departure_time: ex.departure_time || prev.departure_time || "",
          return_date: ex.return_date || prev.return_date,
          return_time: ex.return_time || prev.return_time || "",
          airport_buffer: ex.airport_buffer != null ? String(ex.airport_buffer) : prev.airport_buffer,
          departure_airport: (ex.departure_airport?.name || ex.departure_airport?.place_id) ? ex.departure_airport : prev.departure_airport,
          destination_airport: (ex.destination_airport?.name || ex.destination_airport?.place_id) ? ex.destination_airport : prev.destination_airport,
          return_airport: (ex.return_airport?.name || ex.return_airport?.place_id) ? ex.return_airport : prev.return_airport,
        }));

        // Surface venue candidates for picker UI (additive — does not block
        // the form from being usable with the raw venue_name already set).
        setSmartFillVenueCandidates(Array.isArray(ex.venue_candidates) ? ex.venue_candidates : []);

        // Band / collaborator matcher — fetch fresh to avoid race condition
        // on mount. The component-level `bands` / `collaborators` state is
        // still hydrated by fetchBands / fetchCollaborators on mount (used by
        // the band dropdown and collaborator picker elsewhere in the form);
        // this fresh fetch is additive and only feeds the matcher so a fast
        // Smart Fill response doesn't beat the mount fetches to the punch.
        if (ex.title) {
          try {
            const [bandsRes, collabsRes] = await Promise.all([
              apiClient.get("/bands"),
              apiClient.get("/collaborators"),
            ]);
            const needle = ex.title.trim().toLowerCase();
            const pool = [
              ...(bandsRes.data || []).map(b => b?.name).filter(Boolean),
              ...(collabsRes.data || []).map(c => c?.name).filter(Boolean),
            ];
            const match = pool.find(n => n && n.trim().toLowerCase() === needle);
            if (match && match.trim().toLowerCase() !== ex.title.trim().toLowerCase()) {
              // only prompt if saved name differs (e.g. casing/spacing)
            } else if (match) {
              toast(`Did you mean ${match}?`, {
                duration: 8000,
                action: {
                  label: "Use this name",
                  onClick: () => {
                    setFormData(prev => ({ ...prev, artist_name: match }));
                    toast.success(`Using ${match}`);
                  },
                },
                cancel: { label: "Dismiss", onClick: () => {} },
              });
            }
          } catch (e) {
            // silent fail — band matching is non-critical
          }
        }

        const found = Object.entries(ex)
          .filter(([k, v]) => !["venue_candidates", "is_duplicate", "gig_type"].includes(k) && v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0))
          .map(([k]) => k.replace(/_/g, " "));
        if (found.length === 0) {
          toast.error("AI couldn't extract gig details. Try adding more context.");
        } else {
          toast.success(`AI extracted: ${found.slice(0, 5).join(", ")}${found.length > 5 ? "..." : ""}`);
        }
      }
    } catch (error) {
      console.error("Smart fill error:", error);
      toast.error("Smart Fill failed. Try a different approach.");
    } finally {
      setEnriching(false);
    }
  };

  // Smart Fill venue picker — user clicked one of the Google-Places candidates
  // returned by /gigs/parse-schedule. Mirrors handleSelectPlace's flow: fetch full
  // details for city/website, register/find the venue in shared_venues so we
  // get a stable venue_id, then merge into formData and dismiss the picker.
  const handleSmartFillVenuePick = async (cand) => {
    if (!cand?.place_id) return;
    setSmartFillVenueSaving(true);
    try {
      let detail = null;
      try {
        const res = await apiClient.get(`/maps/places/details?place_id=${cand.place_id}`);
        detail = res.data?.result || null;
      } catch {
        // Place Details may be denied on this key — fall back to the
        // text-search fields we already have. The venue still gets saved.
      }

      let venueId = null;
      try {
        const venueRes = await apiClient.post("/venues", {
          name: detail?.name || cand.name,
          city: detail?.city || "",
          country: detail?.country || "UK",
          postcode: detail?.postcode || "",
          website: detail?.website || "",
          google_maps_link: detail?.google_maps_link || "",
          place_id: cand.place_id,
        });
        venueId = venueRes.data?.venue_id || null;
      } catch {
        // Silent — venue may already exist; we keep place_id either way.
      }

      setFormData(prev => ({
        ...prev,
        venue_name: detail?.name || cand.name || prev.venue_name,
        venue_id: venueId || prev.venue_id,
        venue_address: detail?.address || cand.formatted_address || prev.venue_address,
        city: detail?.city || prev.city,
        country: detail?.country || prev.country,
        venue_website: detail?.website || prev.venue_website,
      }));

      toast.success(`Locked in ${detail?.name || cand.name}`);
      setSmartFillVenueCandidates([]);
    } catch (e) {
      console.error("Smart fill venue pick error:", e);
      toast.error("Couldn't save that venue — keeping the typed name");
    } finally {
      setSmartFillVenueSaving(false);
    }
  };

  // Voice input for Smart Fill
  const [isListening, setIsListening] = useState(false);
  const speechSupported = typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);
  
  const startVoiceInput = () => {
    if (!speechSupported) return;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-GB';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    
    setIsListening(true);
    recognition.start();
    
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setEnrichQuery(transcript);
      setIsListening(false);
      toast.success("Got it! Tap Search to find gig details.");
    };
    recognition.onerror = () => {
      setIsListening(false);
      toast.error("Couldn't hear you. Please try again.");
    };
    recognition.onend = () => setIsListening(false);
  };

  const handleDelete = async () => {
    if (!gigToDelete) return;

    try {
      await apiClient.delete(`/gigs/${gigToDelete}`);
      toast.success("Gig deleted");
      setShowDeleteDialog(false);
      setGigToDelete(null);
      if (mode === "edit") {
        navigate("/gigs");
      } else {
        fetchGigs();
      }
    } catch (error) {
      toast.error("Failed to delete gig");
    }
  };

  // Bulk delete from Select mode — loops the existing single-gig DELETE
  // endpoint (no bulk endpoint on the backend yet).
  const handleBulkDelete = async () => {
    const ids = [...selectedGigIds];
    if (ids.length === 0) return;
    setBulkDeleting(true);
    try {
      await Promise.all(ids.map((id) => apiClient.delete(`/gigs/${id}`)));
      toast.success(`Deleted ${ids.length} gig${ids.length === 1 ? "" : "s"}`);
      setSelectedGigIds(new Set());
      setShowBulkDeleteDialog(false);
      fetchGigs();
    } catch (error) {
      toast.error("Failed to delete some gigs");
    } finally {
      setBulkDeleting(false);
    }
  };


  // Get unique venues and artists for filters (from both lists)
  const allGigs = [...gigs, ...pastGigs];
  const uniqueVenues = [...new Set(allGigs.map(g => g.venue_name).filter(v => v?.trim()))].sort();
  const uniqueArtists = [...new Set(allGigs.map(g => g.artist_name).filter(v => v?.trim()))].sort();

  // Selection is open to every gig now (Delete/Create Template don't care
  // about fee status) — but Add to Invoice still requires every selected
  // gig to be unbilled, so gate that one button on the actual selection.
  const selectedGigsList = [...selectedGigIds].map((id) => allGigs.find((g) => g.gig_id === id)).filter(Boolean);
  const selectedAllInvoiceEligible = selectedGigsList.length > 0 && selectedGigsList.every((g) => g.fee && g.fee_status === "pending");

  // Filter function for gigs
  const filterGigList = (gigList, order) => {
    return gigList
      .filter(gig => {
        if (filterStatus !== "all" && gig.status !== filterStatus) return false;
        if (filterPaymentStatus !== "all" && gig.fee_status !== filterPaymentStatus) return false;
        if (filterVenue !== "all" && gig.venue_name !== filterVenue) return false;
        if (filterArtist !== "all" && gig.artist_name !== filterArtist) return false;
        if (filterVisibility === "public" && gig.is_public === false) return false;
        if (filterVisibility === "private" && gig.is_public !== false) return false;
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          return (
            (gig.artist_name || "").toLowerCase().includes(query) ||
            (gig.venue_name || "").toLowerCase().includes(query) ||
            (gig.city || "").toLowerCase().includes(query)
          );
        }
        return true;
      })
      .sort((a, b) => {
        const dateA = new Date(a.date || 0);
        const dateB = new Date(b.date || 0);
        return order === "asc" ? dateA - dateB : dateB - dateA;
      });
  };

  // Upcoming defaults to chronological (soonest first); Past defaults to
  // most-recent-first -- the two tabs warrant different defaults, so each
  // gets its own sort state rather than sharing one.
  const filteredGigs = filterGigList(gigs, sortOrder);
  const filteredPastGigs = filterGigList(pastGigs, pastSortOrder);

  // List view
  if (mode === "list") {
    return (
      <Layout>
        <div data-testid="gigs-page">
          {/* Header — kicker on its own row, then h1 + Add Gig button
              share a baseline row underneath. Puts the button visually
              aligned with the 'Your Gigs' hero rather than the small
              'GIG MANAGEMENT' eyebrow above it. */}
          <div style={{ marginBottom: tokens.space.xxl }}>
            <Kicker color={tokens.color.orange} style={{ marginBottom: tokens.space.xs }}>
              Gig Management
            </Kicker>
            <div
              className="flex flex-row items-center justify-between"
              style={{ gap: tokens.space.md }}
            >
              <h1
                style={{
                  margin: 0,
                  ...tokens.type.heroSm,
                  color: tokens.color.text,
                  textWrap: "balance",
                  minWidth: 0,
                }}
              >
                Your Gigs
              </h1>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid="add-gig-btn"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: tokens.space.sm,
                      padding: "10px 16px",
                      background: tokens.color.orange,
                      color: tokens.color.onOrange,
                      border: tokens.border.brand,
                      borderRadius: tokens.radius.md + 1,
                      fontFamily: tokens.font.body,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    Add Gig
                    <ChevronDown className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate("/gigs/new")} data-testid="add-gig-blank">
                    <Plus className="h-4 w-4 mr-2" />
                    Add new gig
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowTemplatePicker(true)} data-testid="add-gig-from-template">
                    <LayoutTemplate className="h-4 w-4 mr-2" />
                    Create from template
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* Saved Templates section — view / start-from / rename / delete.
              Only shown after the user clicks "Create from template". */}
          {showTemplatePicker && templates.length > 0 && (
            <div style={{ marginBottom: tokens.space.xl }} data-testid="saved-templates-section">
              <div style={{ fontFamily: tokens.font.mono, fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: tokens.color.textMuted, marginBottom: tokens.space.sm }}>
                Saved Templates
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: tokens.space.sm }}>
                {templates.map((t) => (
                  <div
                    key={t.template_id}
                    style={{
                      display: "flex",
                      flexDirection: isDesktopChips ? "row" : "column",
                      alignItems: isDesktopChips ? "center" : "stretch",
                      gap: isDesktopChips ? 6 : 2,
                      padding: isDesktopChips ? "5px 6px 5px 12px" : "8px 10px",
                      borderRadius: isDesktopChips ? tokens.radius.pill : tokens.radius.md,
                      background: tokens.color.innerBg, border: tokens.border.inner,
                      width: isDesktopChips ? "auto" : "100%",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => startGigFromTemplate(t)}
                      title="Start a gig from this template"
                      data-testid={`template-chip-${t.template_id}`}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        background: "transparent", border: "none", color: tokens.color.text, cursor: "pointer",
                        fontWeight: 600, fontSize: 13, maxWidth: isDesktopChips ? 220 : "none",
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        padding: 0,
                      }}
                    >
                      <LayoutTemplate className="h-3.5 w-3.5" style={{ color: tokens.color.orange, flexShrink: 0 }} />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.name}</span>
                    </button>
                    {/* Action icons: stay inline with the name on desktop;
                        drop to their own row on mobile so a narrow screen
                        never clips one off — width overflow, not just a
                        visual nit (see ops-playbook #11 for the family of
                        "fits on desktop, clips on a real phone" bugs). */}
                    <div
                      style={{
                        display: "flex",
                        gap: isDesktopChips ? 0 : 4,
                        justifyContent: isDesktopChips ? undefined : "flex-end",
                        flexShrink: 0,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setTemplateDatesSource(t)}
                        aria-label="Add multiple dates from this template"
                        title="Add multiple dates"
                        data-testid={`template-multi-date-${t.template_id}`}
                        style={{ width: 26, height: 26, display: "grid", placeItems: "center", background: "transparent", border: "none", borderRadius: tokens.radius.sm, color: tokens.color.textDim, cursor: "pointer" }}
                      >
                        <CalendarPlus className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRenameTemplate(t.template_id, t.name)}
                        aria-label="Rename template"
                        title="Rename"
                        style={{ width: 26, height: 26, display: "grid", placeItems: "center", background: "transparent", border: "none", borderRadius: tokens.radius.sm, color: tokens.color.textDim, cursor: "pointer" }}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteTemplate(t.template_id)}
                        aria-label="Delete template"
                        title="Delete"
                        style={{ width: 26, height: 26, display: "grid", placeItems: "center", background: "transparent", border: "none", borderRadius: tokens.radius.sm, color: tokens.color.textDim, cursor: "pointer" }}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Gigs Tabs — unified toolbar row: tabs + search + filter button.
              Replaces the previous 3-row arrangement (search row, desktop
              Select dropdowns, tabs). All filter dropdowns now live in the
              bottom sheet that opens via the Filters button. */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div
              className="flex flex-wrap items-center mb-6"
              style={{ gap: tokens.space.md }}
            >
              <TabsList
                className="grid grid-cols-2 w-full md:w-[280px]"
                style={{ flexShrink: 0 }}
              >
                <TabsTrigger value="upcoming" className="flex items-center gap-2" data-testid="upcoming-tab">
                  <Clock className="h-4 w-4" />
                  Upcoming ({gigs.length})
                </TabsTrigger>
                <TabsTrigger value="past" className="flex items-center gap-2" data-testid="past-tab">
                  <History className="h-4 w-4" />
                  Past ({pastGigs.length})
                </TabsTrigger>
              </TabsList>

              <div className="relative" style={{ flex: "1 1 200px", minWidth: 160 }}>
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 z-10" style={{ color: tokens.color.textMuted }} />
                <ClearableInput
                  placeholder="Search gigs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-gigs"
                />
              </div>

              <button
                type="button"
                onClick={() => setShowFiltersSheet(true)}
                data-testid="filters-btn"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: tokens.space.sm,
                  padding: "8px 14px",
                  background: "transparent",
                  color: tokens.color.textDim,
                  border: tokens.border.card,
                  borderRadius: tokens.radius.md + 1,
                  fontFamily: tokens.font.body,
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  position: "relative",
                }}
              >
                <SlidersHorizontal className="h-4 w-4" />
                Filters
                {((activeTab === "past" ? pastSortOrder !== "desc" : sortOrder !== "asc") || filterStatus !== "all" || filterPaymentStatus !== "all" || filterVisibility !== "all" || filterArtist !== "all" || filterVenue !== "all") && (
                  <span
                    aria-hidden="true"
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: tokens.radius.pill,
                      background: tokens.color.orange,
                      flexShrink: 0,
                    }}
                  />
                )}
              </button>
            </div>

            <TabsContent value="upcoming">
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3, 4, 5, 6].map(i => (
                    <div
                      key={i}
                      className="animate-pulse"
                      style={{
                        height: 192,
                        background: tokens.color.cardBg,
                        border: tokens.border.card,
                        borderRadius: tokens.radius.xl,
                      }}
                    />
                  ))}
                </div>
              ) : filteredGigs.length === 0 ? (
                <EmptyGigState
                  icon={Clock}
                  message={
                    searchQuery || filterStatus !== "all" || filterPaymentStatus !== "all"
                      ? "No upcoming gigs match your filters"
                      : "No upcoming gigs. Add your next gig to get started!"
                  }
                  showCta={!searchQuery && filterStatus === "all" && filterPaymentStatus === "all"}
                  ctaLabel="Add Your First Gig"
                  ctaTestId="add-first-gig"
                  onCta={() => navigate("/gigs/new")}
                />
              ) : (
                <MonthGroupedGigs
                  groups={groupGigsByMonth(filteredGigs)}
                  renderItem={(gig) => (
                    <GigCard
                      key={gig.gig_id}
                      gig={gig}
                      onEdit={(g) => navigate(`/gigs/${g.gig_id}/edit`)}
                      onDuplicate={(g) => setDuplicateSource(g)}
                      highlighted={highlightGigId === gig.gig_id}
                      selectable
                      selected={selectedGigIds.has(gig.gig_id)}
                      onSelectToggle={(id) => setSelectedGigIds(prev => {
                        const next = new Set(prev);
                        if (next.has(id)) next.delete(id); else next.add(id);
                        return next;
                      })}
                    />
                  )}
                />
              )}
            </TabsContent>

            <TabsContent value="past">
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {[1, 2, 3, 4, 5, 6].map(i => (
                    <div
                      key={i}
                      className="animate-pulse"
                      style={{
                        height: 192,
                        background: tokens.color.cardBg,
                        border: tokens.border.card,
                        borderRadius: tokens.radius.xl,
                      }}
                    />
                  ))}
                </div>
              ) : filteredPastGigs.length === 0 ? (
                <EmptyGigState
                  icon={History}
                  message={
                    searchQuery || filterStatus !== "all" || filterPaymentStatus !== "all"
                      ? "No past gigs match your filters"
                      : "No past gigs yet. Your completed gigs will appear here."
                  }
                />
              ) : (
                <MonthGroupedGigs
                  groups={groupGigsByMonth(filteredPastGigs)}
                  renderItem={(gig) => (
                    <GigCard
                      key={gig.gig_id}
                      gig={gig}
                      onEdit={(g) => navigate(`/gigs/${g.gig_id}/edit`)}
                      onDuplicate={(g) => setDuplicateSource(g)}
                      highlighted={highlightGigId === gig.gig_id}
                      selectable
                      selected={selectedGigIds.has(gig.gig_id)}
                      onSelectToggle={(id) => setSelectedGigIds(prev => {
                        const next = new Set(prev);
                        if (next.has(id)) next.delete(id); else next.add(id);
                        return next;
                      })}
                    />
                  )}
                />
              )}
            </TabsContent>
          </Tabs>
        </div>

        {/* Floating action bar — appears whenever ≥1 gig is selected via the
            per-card checkboxes (always available, no separate Select mode).
            Add to Invoice POSTs all selected gig_ids as one bundle (backend
            validates compatibility and rejects with a clear toast if they
            don't share a recipient) — disabled unless every selected gig is
            still unbilled. Duplicate (copy onto new dates) and Create Template
            (save as a reusable preset) both act on a single gig, so they're
            only enabled for a single selection. Delete loops the single-gig
            DELETE endpoint client-side (no bulk endpoint) regardless of fee
            status. An X at the far right clears the selection. */}
        {selectedGigIds.size > 0 && (
          <div
            style={{
              position: "fixed",
              // Clear the mobile bottom nav (~64px + the phone's safe-area
              // inset) so the bar sits above it; on desktop there's no nav,
              // so a plain 24px gap. zIndex above the nav's 9999 as a
              // belt-and-braces guard against any overlap on odd viewports.
              bottom: isDesktopNav ? 24 : "calc(env(safe-area-inset-bottom, 0px) + 76px)",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 10000,
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
              justifyContent: "center",
              padding: "12px 16px",
              // Keep the bar within the viewport on narrow phones instead of
              // overflowing past the screen edges.
              width: "max-content",
              maxWidth: "calc(100vw - 24px)",
              background: tokens.color.cardBg,
              border: `1px solid ${tokens.color.orange}`,
              borderRadius: tokens.radius.lg,
              boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
            }}
            data-testid="gig-select-action-bar"
          >
            <span style={{ fontSize: 14, color: tokens.color.text }}>
              <strong>{selectedGigIds.size}</strong> gig{selectedGigIds.size === 1 ? "" : "s"} selected
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                const ids = [...selectedGigIds];
                if (ids.length === 0) return;
                setBundling(true);
                try {
                  // Pull recipient defaults from the first gig (matches the
                  // backend's "all gigs share a recipient" rule — first gig's
                  // venue/band contact is therefore correct for the bundle).
                  const allGigs = [...gigs, ...pastGigs];
                  const first = allGigs.find(g => g.gig_id === ids[0]);
                  let recipientName = first?.venue_name || "";
                  let recipientEmail = "";
                  if (first?.band_id) {
                    try {
                      const b = (await apiClient.get(`/bands/${first.band_id}`)).data;
                      recipientName = b.contact_name || recipientName;
                      recipientEmail = b.contact_email || "";
                    } catch {}
                  }
                  if (!recipientEmail && first?.venue_id) {
                    try {
                      const v = (await apiClient.get(`/venues/${first.venue_id}`)).data;
                      recipientName = v.contact_name || recipientName;
                      recipientEmail = v.contact_email || "";
                    } catch {}
                  }
                  const res = await apiClient.post("/invoices", {
                    gig_ids: ids,
                    recipient_type: "venue",
                    recipient_name: recipientName,
                    recipient_email: recipientEmail || null,
                    recipient_address: null,
                    line_items: [],  // backend auto-builds from gig_ids
                    vat_rate: 0,
                    currency: "GBP",
                  });
                  toast.success(`Bundled ${ids.length} gigs into one invoice`);
                  setSelectedGigIds(new Set());
                  fetchGigs();
                  navigate(`/invoices/${res.data?.invoice_id}`);
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Failed to bundle gigs");
                } finally {
                  setBundling(false);
                }
              }}
              disabled={!selectedAllInvoiceEligible || bundling || bulkDeleting}
              title={!selectedAllInvoiceEligible ? "Only unbilled gigs (fee set, not yet invoiced) can be added to an invoice" : undefined}
              data-testid="invoice-bundle-submit"
            >
              {bundling ? "Adding..." : "Add to Invoice"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={selectedGigIds.size !== 1 || bundling || bulkDeleting}
              title={selectedGigIds.size !== 1 ? "Select exactly one gig to duplicate" : undefined}
              onClick={() => {
                const allGigs = [...gigs, ...pastGigs];
                const gig = allGigs.find((g) => g.gig_id === [...selectedGigIds][0]);
                if (gig) setDuplicateSource(gig);
              }}
              data-testid="bulk-duplicate"
            >
              Duplicate
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={selectedGigIds.size !== 1 || bundling || bulkDeleting}
              title={selectedGigIds.size !== 1 ? "Select exactly one gig to share" : undefined}
              onClick={() => {
                const allGigs = [...gigs, ...pastGigs];
                const gig = allGigs.find((g) => g.gig_id === [...selectedGigIds][0]);
                if (gig) setShareDialog({ gig, kind: "family" });
              }}
              data-testid="bulk-share-gig"
            >
              Share gig
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={selectedGigIds.size !== 1 || bundling || bulkDeleting}
              title={selectedGigIds.size !== 1 ? "Select exactly one gig to send as a deputy job" : undefined}
              onClick={() => {
                const allGigs = [...gigs, ...pastGigs];
                const gig = allGigs.find((g) => g.gig_id === [...selectedGigIds][0]);
                if (gig) setShareDialog({ gig, kind: "dep" });
              }}
              data-testid="bulk-dep-this-out"
            >
              Dep this out
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={selectedGigIds.size !== 1 || bundling || bulkDeleting}
              title={selectedGigIds.size !== 1 ? "Select exactly one gig to save as a template" : undefined}
              onClick={() => {
                const allGigs = [...gigs, ...pastGigs];
                const gig = allGigs.find((g) => g.gig_id === [...selectedGigIds][0]);
                if (gig) setTemplateNameDialog({ gig, name: gig.artist_name || "" });
              }}
              data-testid="bulk-create-template"
            >
              Create Template
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowBulkDeleteDialog(true)}
              disabled={bundling || bulkDeleting}
              data-testid="bulk-delete-trigger"
            >
              Delete
            </Button>
            {/* Dismiss — X at the far right clears the selection */}
            <button
              type="button"
              onClick={() => setSelectedGigIds(new Set())}
              disabled={bundling || bulkDeleting}
              aria-label="Clear selection"
              title="Clear selection"
              style={{
                marginLeft: 4, width: 32, height: 32, display: "grid", placeItems: "center",
                background: "transparent", border: "none", borderRadius: tokens.radius.md,
                color: tokens.color.textDim, cursor: "pointer",
              }}
            >
              <X style={{ width: 16, height: 16 }} />
            </button>
          </div>
        )}

        {/* Bulk delete confirmation */}
        <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete {selectedGigIds.size} gig{selectedGigIds.size === 1 ? "" : "s"}?</DialogTitle>
              <DialogDescription>
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBulkDeleteDialog(false)} disabled={bulkDeleting}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleBulkDelete} disabled={bulkDeleting} data-testid="confirm-bulk-delete">
                {bulkDeleting ? "Deleting..." : "Delete"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Share dialog: WhatsApp or text for a gig (family link or deputy pack) */}
        <GigShareDialog
          gig={shareDialog?.gig}
          kind={shareDialog?.kind || "family"}
          open={!!shareDialog}
          onClose={() => setShareDialog(null)}
          onPublicEnabled={(token) => {
            // Keep this page's share state in step when the shared gig is the one being edited
            if (shareDialog?.gig?.gig_id === id) { setShareEnabled(true); setShareToken(token); }
          }}
        />

        {/* Duplicate / template dialog */}
        <DuplicateGigDialog
          source={duplicateSource}
          open={!!duplicateSource}
          onClose={() => setDuplicateSource(null)}
          allGigs={[...gigs, ...pastGigs]}
          onRefetch={fetchGigs}
        />

        {/* Add multiple dates from a saved template */}
        <DuplicateGigDialog
          template={templateDatesSource}
          open={!!templateDatesSource}
          onClose={() => setTemplateDatesSource(null)}
          allGigs={[...gigs, ...pastGigs]}
          onRefetch={fetchGigs}
        />

        {/* Name-this-template dialog (from the selection bar's Create Template) */}
        <Dialog open={!!templateNameDialog} onOpenChange={(o) => !o && !savingTemplate && setTemplateNameDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Save as template</DialogTitle>
              <DialogDescription>
                Saves this gig's details (venue, fee, dress code, band, setlist…) as a reusable
                template. Dates and payment status aren't saved.
              </DialogDescription>
            </DialogHeader>
            <div style={{ padding: "4px 0" }}>
              <Label htmlFor="template-name">Template name</Label>
              <Input
                id="template-name"
                value={templateNameDialog?.name || ""}
                onChange={(e) => setTemplateNameDialog((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Wedding — 4-piece"
                autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") handleCreateTemplate(); }}
                data-testid="template-name-input"
                style={{ marginTop: 6 }}
              />
            </div>
            <DialogFooter>
              <Button onClick={handleCreateTemplate} disabled={savingTemplate} data-testid="template-name-save">
                {savingTemplate ? "Saving…" : "Save template"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Template picker (from the Add Gig dropdown's "Create from template") */}
        <Dialog open={showTemplatePicker} onOpenChange={setShowTemplatePicker}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Start from a template</DialogTitle>
              <DialogDescription>
                Pick a saved template to prefill a new gig. You'll just set the date.
              </DialogDescription>
            </DialogHeader>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "4px 0", maxHeight: 360, overflowY: "auto" }}>
              {templates.length === 0 ? (
                <p style={{ fontSize: 13, color: tokens.color.textMuted, margin: "8px 0" }}>
                  No saved templates yet. Select a gig and choose “Create Template” to make one.
                </p>
              ) : (
                templates.map((t) => (
                  <div key={t.template_id} style={{ display: "flex", alignItems: "center", gap: 6, width: "100%" }}>
                    <button
                      type="button"
                      onClick={() => startGigFromTemplate(t)}
                      data-testid={`pick-template-${t.template_id}`}
                      style={{
                        display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0, textAlign: "left",
                        padding: "10px 12px", borderRadius: tokens.radius.md,
                        background: tokens.color.innerBg, border: tokens.border.inner,
                        color: tokens.color.text, cursor: "pointer",
                      }}
                    >
                      <LayoutTemplate className="h-4 w-4" style={{ flexShrink: 0, color: tokens.color.orange }} />
                      <span style={{ minWidth: 0, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600, fontSize: 14 }}>
                        {t.name}
                      </span>
                      <span style={{ fontSize: 12, color: tokens.color.textMuted, whiteSpace: "nowrap" }}>
                        {t.fields?.venue_name || ""}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowTemplatePicker(false); setTemplateDatesSource(t); }}
                      aria-label="Add multiple dates from this template"
                      title="Add multiple dates"
                      data-testid={`pick-template-multi-date-${t.template_id}`}
                      style={{
                        width: 40, height: 40, flexShrink: 0, display: "grid", placeItems: "center",
                        background: "transparent", border: tokens.border.inner, borderRadius: tokens.radius.md,
                        color: tokens.color.textDim, cursor: "pointer",
                      }}
                    >
                      <CalendarPlus className="h-4 w-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Gig</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this gig? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete">
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Filter bottom sheet — universal across mobile and desktop now.
            zIndex above the mobile bottom nav's 9999 so the sheet (and its
            Apply Filters button at the bottom edge) sits over the nav rather
            than behind it — otherwise the nav covered the Apply button. */}
        {showFiltersSheet && (
          <div className="fixed inset-0" style={{ zIndex: 10000 }} onClick={() => setShowFiltersSheet(false)}>
            <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.6)" }} />
            <div
              className="absolute bottom-0 left-0 right-0 animate-slide-up"
              style={{
                background: tokens.color.cardBg,
                borderTopLeftRadius: tokens.radius.xxl,
                borderTopRightRadius: tokens.radius.xxl,
                borderTop: tokens.border.card,
                paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              }}
              onClick={e => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: `${tokens.space.lg}px ${tokens.space.xl}px ${tokens.space.md}px`,
                }}
              >
                <Kicker size={16} color={tokens.color.text}>Filters</Kicker>
                <button
                  onClick={() => setShowFiltersSheet(false)}
                  aria-label="Close filters"
                  style={{
                    background: "transparent",
                    border: "none",
                    color: tokens.color.textMuted,
                    cursor: "pointer",
                    padding: 4,
                  }}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div style={{ padding: `0 ${tokens.space.lg}px ${tokens.space.xl}px`, display: "flex", flexDirection: "column", gap: tokens.space.md }}>
                <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.xs }}>
                  <Kicker size={11}>Sort Order</Kicker>
                  <Select
                    value={activeTab === "past" ? pastSortOrder : sortOrder}
                    onValueChange={activeTab === "past" ? setPastSortOrder : setSortOrder}
                  >
                    <SelectTrigger className="w-full" data-testid="sort-order">
                      <SelectValue placeholder="Sort order" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asc">Upcoming First</SelectItem>
                      <SelectItem value="desc">Recent First</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.xs }}>
                  <Kicker size={11}>Status</Kicker>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-full" data-testid="filter-status">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.xs }}>
                  <Kicker size={11}>Payment Status</Kicker>
                  <Select value={filterPaymentStatus} onValueChange={setFilterPaymentStatus}>
                    <SelectTrigger className="w-full" data-testid="filter-payment-status">
                      <SelectValue placeholder="Payment Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Payment Statuses</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="invoiced">Invoiced</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.xs }}>
                  <Kicker size={11}>Visibility</Kicker>
                  <Select value={filterVisibility} onValueChange={setFilterVisibility}>
                    <SelectTrigger className="w-full" data-testid="filter-visibility">
                      <SelectValue placeholder="Visibility" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Gigs</SelectItem>
                      <SelectItem value="public">Public Only</SelectItem>
                      <SelectItem value="private">Private Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {uniqueArtists.length > 1 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.xs }}>
                    <Kicker size={11}>Artist</Kicker>
                    <Select value={filterArtist} onValueChange={setFilterArtist}>
                      <SelectTrigger className="w-full" data-testid="filter-artist">
                        <SelectValue placeholder="Artist/Band" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Artists</SelectItem>
                        {uniqueArtists.map(artist => artist ? (
                          <SelectItem key={artist} value={artist}>{artist}</SelectItem>
                        ) : null)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {uniqueVenues.length > 1 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: tokens.space.xs }}>
                    <Kicker size={11}>Venue</Kicker>
                    <Select value={filterVenue} onValueChange={setFilterVenue}>
                      <SelectTrigger className="w-full" data-testid="filter-venue">
                        <SelectValue placeholder="Venue" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Venues</SelectItem>
                        {uniqueVenues.map(venue => venue ? (
                          <SelectItem key={venue} value={venue}>{venue}</SelectItem>
                        ) : null)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setShowFiltersSheet(false)}
                  data-testid="filters-apply-btn"
                  style={{
                    width: "100%",
                    marginTop: tokens.space.sm,
                    padding: "12px 16px",
                    background: tokens.color.orange,
                    color: tokens.color.onOrange,
                    border: tokens.border.brand,
                    borderRadius: tokens.radius.md + 1,
                    fontFamily: tokens.font.body,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </Layout>
    );
  }

  // Create/Edit form
  return (
    <Layout>
      <div data-testid={mode === "edit" ? "edit-gig-page" : "create-gig-page"}>
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate("/gigs")} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-1">
              {mode === "edit" ? "Edit Gig" : "New Gig"}
            </p>
            <h1 className="font-heading text-2xl font-bold tracking-tight">
              {mode === "edit" ? formData.artist_name || "Edit Gig" : "Add New Gig"}
            </h1>
          </div>
        </div>

        {loading ? (
          <div className="max-w-2xl space-y-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-16 bg-card border border-border rounded-lg animate-pulse" />
            ))}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="max-w-2xl lg:max-w-6xl space-y-6">
            {/* Smart Fill - Web Search */}
            {(mode === "create" || mode === "edit") && (
              <Card className="bg-gradient-to-br from-primary/10 to-secondary border-primary/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="h-5 w-5 text-primary" />
                    <span className="font-heading font-semibold">Smart Fill</span>
                  </div>
                  <div className="flex gap-2">
                    <ClearableInput
                      value={enrichQuery}
                      onChange={(e) => setEnrichQuery(e.target.value)}
                      placeholder="Paste URL, keywords, or booking message..."
                      className="flex-1"
                      data-testid="smart-fill-input"
                    />
                    {speechSupported && (
                      <Button
                        type="button"
                        variant={isListening ? "default" : "outline"}
                        size="icon"
                        onClick={startVoiceInput}
                        disabled={isListening || enriching}
                        className={isListening ? "bg-primary text-primary-foreground animate-pulse" : ""}
                        data-testid="voice-input-btn"
                      >
                        <Mic className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      type="button"
                      onClick={handleSmartFill}
                      disabled={enriching}
                      className="bg-primary text-primary-foreground"
                      data-testid="smart-fill-btn"
                    >
                      {enriching ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {enrichQuery.trim().length > 60 ? 'Extracting...' : 'Searching...'}
                        </>
                      ) : (
                        <>
                          <Search className="h-4 w-4 mr-2" />
                          Search
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Smart Fill venue picker — shown after AI extracts a venue
                      and Google Places returned candidates. Selecting one
                      registers it via /venues so we get a stable venue_id
                      (mirrors handleSelectPlace's pattern). "None of these"
                      keeps the raw AI-extracted venue_name as plain text. */}
                  {smartFillVenueCandidates.length > 0 && (
                    <div className="mt-3 space-y-2" data-testid="smart-fill-venue-picker">
                      <p className="text-xs text-muted-foreground">
                        Found possible venues — pick one to lock in the address & map:
                      </p>
                      <div className="space-y-1.5">
                        {smartFillVenueCandidates.map((cand) => (
                          <button
                            key={cand.place_id}
                            type="button"
                            onClick={() => handleSmartFillVenuePick(cand)}
                            disabled={smartFillVenueSaving}
                            className="w-full text-left p-2 rounded-md bg-secondary/40 hover:bg-secondary/70 border border-border transition-colors disabled:opacity-50"
                            data-testid={`smart-fill-venue-${cand.place_id}`}
                          >
                            <p className="text-sm font-medium truncate">{cand.name}</p>
                            {cand.formatted_address && (
                              <p className="text-xs text-muted-foreground truncate">{cand.formatted_address}</p>
                            )}
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => setSmartFillVenueCandidates([])}
                          disabled={smartFillVenueSaving}
                          className="w-full text-left p-2 rounded-md hover:bg-secondary/40 border border-dashed border-border text-xs text-muted-foreground transition-colors disabled:opacity-50"
                          data-testid="smart-fill-venue-none"
                        >
                          None of these — keep the typed name
                        </button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Multi-date Smart Fill review. Opens when a paste contained more
                than one dated event; each confirmed row becomes an ordinary gig. */}
            <SmartFillScheduleReview
              open={scheduleReviewOpen}
              onOpenChange={setScheduleReviewOpen}
              parsed={scheduleParsed}
              onCreated={() => {
                setScheduleParsed(null);
                navigate("/gigs");
              }}
            />

            <Card className="bg-card border-border">
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-lg">Gig Details</CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0 md:p-6 md:pt-0 space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">

                  {/* Left column — essential info */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: tokens.space.md,
                    ...(isWideForm ? {
                      padding: 16,
                      background: tokens.color.cardBg,
                      border: tokens.border.card,
                      borderRadius: tokens.radius.lg,
                    } : {}),
                  }}>
                <h3 className="text-sm font-medium mb-4" style={{ color: tokens.color.orange }}>The Essentials</h3>
                {/* Artist / Band Name — smart search */}
                <div className="space-y-2 relative">
                  <Label htmlFor="artist_name">Artist / Band Name *</Label>
                  <ClearableInput
                    id="artist_name"
                    value={formData.artist_name}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        artist_name: val,
                        ...(val === "" ? { band_id: "", performing_member_ids: [] } : {})
                      }));
                      setShowArtistDropdown(val.length >= 1);
                    }}
                    onFocus={() => { if (formData.artist_name.length >= 1) setShowArtistDropdown(true); }}
                    onBlur={() => {
                      setTimeout(() => { setShowArtistDropdown(false); }, 150);
                    }}
                    required
                    autoComplete="off"
                    data-testid="input-artist-name"
                  />
                  {showArtistDropdown && formData.artist_name.trim().length >= 1 && (() => {
                    const q = formData.artist_name.trim().toLowerCase();
                    const matchedBands = bands.filter(b => b.name.toLowerCase().includes(q));
                    const matchedCollabs = collaborators.filter(c => c.name.toLowerCase().includes(q));
                    if (matchedBands.length === 0 && matchedCollabs.length === 0) return null;
                    return (
                      <div className="absolute z-50 left-0 right-0 top-full mt-1 border rounded-lg bg-background shadow-lg max-h-52 overflow-y-auto">
                        {matchedBands.length > 0 && (
                          <>
                            <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border flex items-center gap-1"><Users className="h-3 w-3" /> Bands</div>
                            {matchedBands.map(b => (
                              <button
                                key={b.band_id}
                                type="button"
                                // onPointerDown (not onMouseDown) — fires reliably
                                // before input blur on iOS Safari touch.
                                onPointerDown={(e) => {
                                  e.preventDefault();
                                  const memberIds = (b.members || []).map(m => m.collaborator_id);
                                  setFormData(prev => ({
                                    ...prev,
                                    artist_name: b.name,
                                    band_id: b.band_id,
                                    performing_member_ids: [],
                                    collaborator_ids: memberIds
                                  }));
                                  setShowArtistDropdown(false);
                                  toast.success(`Selected band: ${b.name}${memberIds.length > 0 ? ` (${memberIds.length} musicians added)` : ''}`);
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-secondary text-sm flex items-center gap-2"
                              >
                                <Users className="h-3.5 w-3.5 text-primary shrink-0" />
                                <span className="font-medium">{b.name}</span>
                                <span className="text-muted-foreground text-xs ml-auto">{b.members?.length || 0} members</span>
                              </button>
                            ))}
                          </>
                        )}
                        {matchedCollabs.length > 0 && (
                          <>
                            <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border flex items-center gap-1"><User className="h-3 w-3" /> People</div>
                            {matchedCollabs.slice(0, 8).map(c => (
                              <button
                                key={c.collaborator_id}
                                type="button"
                                onPointerDown={(e) => {
                                  e.preventDefault();
                                  setFormData(prev => ({ ...prev, artist_name: c.name, band_id: "" }));
                                  setShowArtistDropdown(false);
                                  toast.success(`Selected: ${c.name}`);
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-secondary text-sm flex items-center gap-2"
                              >
                                <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                <span>{c.name}</span>
                                {c.instrument && <span className="text-muted-foreground text-xs ml-auto">{c.instrument}</span>}
                              </button>
                            ))}
                          </>
                        )}
                      </div>
                    );
                  })()}
                  {artistQuickAdd && formData.artist_name.trim() && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground">Not found —</span>
                      {["Band", "Person"].map(type => (
                        <button
                          key={type}
                          type="button"
                          disabled={artistAdding}
                          className="text-xs text-primary hover:underline disabled:opacity-50"
                          onClick={async () => {
                            setArtistAdding(true);
                            try {
                              const name = formData.artist_name.trim();
                              if (type === "Band") {
                                const res = await apiClient.post("/bands", { name });
                                setBands(prev => [...prev, res.data]);
                                setFormData(prev => ({ ...prev, band_id: res.data.band_id }));
                                toast.success(`Added band "${name}"`);
                              } else {
                                const res = await apiClient.post("/collaborators", { name });
                                setCollaborators(prev => [...prev, res.data]);
                                toast.success(`Added person "${name}"`);
                              }
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Failed to add");
                            } finally {
                              setArtistAdding(false);
                            }
                          }}
                          data-testid={`quick-add-${type.toLowerCase()}`}
                        >
                          + Add as {type}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Date & Time */}
                <div className="space-y-3">
                  {/* Date row with multi-day toggle */}
                  <div className="flex items-flex-start gap-4">
                    <div className="flex-1 space-y-2">
                      <Label>Start Date *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !formData.date && "text-muted-foreground"
                            )}
                            data-testid="date-picker-trigger"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.date ? format(formData.date, "PPP") : "Pick a date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={formData.date}
                            onSelect={(date) => setFormData({ ...formData, date })}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div className="flex items-center gap-2 pt-6">
                      <Label className="text-xs text-muted-foreground">Multi-day</Label>
                      <Switch
                        checked={!!formData.is_multiday}
                        onCheckedChange={(checked) =>
                          setFormData((prev) => ({ ...prev, is_multiday: checked }))
                        }
                        data-testid="switch-is-multiday"
                      />
                    </div>
                  </div>

                  {/* End date — optional; for gigs that span multiple calendar
                      days (a two-day festival, a residency). This is the
                      calendar-span kind of multi-day, separate from the flights
                      "Multi-day" toggle above. */}
                  <div className="flex items-flex-start gap-4">
                    <div className="flex-1 space-y-2">
                      <Label className="flex items-center gap-2">
                        End Date
                        <span className="text-xs font-normal text-muted-foreground">(optional — multi-day gigs)</span>
                      </Label>
                      <div className="flex gap-2">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "flex-1 justify-start text-left font-normal",
                                !formData.end_date && "text-muted-foreground"
                              )}
                              data-testid="end-date-picker-trigger"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {formData.end_date ? format(formData.end_date, "PPP") : "Same day"}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={formData.end_date}
                              onSelect={(d) => setFormData((prev) => ({ ...prev, end_date: d }))}
                              disabled={(d) => formData.date && d < formData.date}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        {formData.end_date && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="shrink-0"
                            onClick={() => setFormData((prev) => ({ ...prev, end_date: null }))}
                            aria-label="Clear end date"
                            data-testid="clear-end-date"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Multi-day flight/travel details — revealed when toggle is on */}
                  {formData.is_multiday && (
                    <div className="space-y-4">
                      {/* Airport buffer — applies to both legs */}
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Airport buffer (min before flight)</Label>
                        <Input
                          type="number"
                          placeholder="e.g. 120"
                          min="0"
                          value={formData.airport_buffer || ""}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, airport_buffer: e.target.value }))
                          }
                          data-testid="input-airport-buffer"
                        />
                      </div>

                      {/* Outbound */}
                      <div className="space-y-2 pt-2">
                        <Label className="text-xs font-medium">Outbound</Label>
                        <div className="grid grid-cols-2 gap-2 [&>div]:min-w-0 [&_input]:min-w-0">
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Travel date</Label>
                            <Input
                              type="date"
                              value={formData.travel_date || ""}
                              onChange={(e) =>
                                setFormData((prev) => ({ ...prev, travel_date: e.target.value || null }))
                              }
                              data-testid="input-travel-date"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Departure time</Label>
                            <Input
                              type="time"
                              value={formData.departure_time || ""}
                              onChange={(e) =>
                                setFormData((prev) => ({ ...prev, departure_time: e.target.value || "" }))
                              }
                              data-testid="input-departure-time"
                            />
                          </div>
                        </div>
                        <AirportPicker
                          label="From"
                          value={formData.departure_airport}
                          onChange={(v) => setFormData((prev) => ({ ...prev, departure_airport: v }))}
                          testId="input-departure-airport"
                        />
                        <AirportPicker
                          label="To"
                          value={formData.destination_airport}
                          onChange={(v) => setFormData((prev) => ({ ...prev, destination_airport: v }))}
                          testId="input-destination-airport"
                        />
                      </div>

                      {/* Return / Inbound */}
                      <div className="space-y-2 pt-2">
                        <Label className="text-xs font-medium">Inbound</Label>
                        <div className="grid grid-cols-2 gap-2 [&>div]:min-w-0 [&_input]:min-w-0">
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Return date</Label>
                            <Input
                              type="date"
                              value={formData.return_date || ""}
                              onChange={(e) =>
                                setFormData((prev) => ({ ...prev, return_date: e.target.value || null }))
                              }
                              data-testid="input-return-date"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Departure time</Label>
                            <Input
                              type="time"
                              value={formData.return_time || ""}
                              onChange={(e) =>
                                setFormData((prev) => ({ ...prev, return_time: e.target.value || "" }))
                              }
                              data-testid="input-return-time"
                            />
                          </div>
                        </div>
                        <AirportPicker
                          label="To (if different)"
                          value={formData.return_airport}
                          onChange={(v) => setFormData((prev) => ({ ...prev, return_airport: v }))}
                          placeholder="Same as departure…"
                          testId="input-return-airport"
                        />
                      </div>
                    </div>
                  )}
                  {/* Time row — stack on mobile; iOS native time inputs have a
                      large intrinsic min-width and overflow/overlap in a 2-col
                      grid on narrow screens, so only go 2-up from sm. */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2 min-w-0">
                      <Label htmlFor="start_time">Start Time</Label>
                      <Input
                        id="start_time"
                        type="time"
                        className="min-w-0 appearance-none max-w-full"
                        style={{ WebkitAppearance: "none" }}
                        value={formData.start_time}
                        onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                        onBlur={(e) => {
                          const formatted = formatTimeInput(e.target.value);
                          if (formatted !== e.target.value) {
                            setTimeout(() => {
                              setFormData(prev => ({ ...prev, start_time: formatted }));
                            }, 0);
                          }
                        }}
                        data-testid="input-start-time"
                      />
                    </div>
                    <div className="space-y-2 min-w-0">
                      <Label htmlFor="end_time">End Time</Label>
                      <Input
                        id="end_time"
                        type="time"
                        className="min-w-0 appearance-none max-w-full"
                        style={{ WebkitAppearance: "none" }}
                        value={formData.end_time}
                        onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                        onBlur={(e) => {
                          const formatted = formatTimeInput(e.target.value);
                          if (formatted !== e.target.value) {
                            setTimeout(() => {
                              setFormData(prev => ({ ...prev, end_time: formatted }));
                            }, 0);
                          }
                        }}
                        data-testid="input-end-time"
                      />
                    </div>
                  </div>
                </div>

                {/* Venue & Call Time */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 relative">
                    <Label htmlFor="venue_name">Venue Name *</Label>
                    <Input
                      id="venue_name"
                      value={formData.venue_name}
                      onChange={(e) => {
                        const val = e.target.value;
                        setFormData({ ...formData, venue_name: val });
                        setVenueQuery(val);
                      }}
                      onFocus={() => { if (venueResults.length > 0) setShowVenueDropdown(true); }}
                      onBlur={() => setTimeout(() => setShowVenueDropdown(false), 200)}
                      required
                      autoComplete="off"
                      data-testid="input-venue-name"
                    />
                    {showVenueDropdown && (venueResults.length > 0 || placesResults.length > 0 || (venueQuery.length >= 2 && !venueSearching)) && (
                      <div className="absolute z-50 left-0 right-0 top-full mt-1 border rounded-lg bg-background shadow-lg max-h-64 overflow-y-auto" data-testid="venue-autocomplete-dropdown">
                        {/* Local venue results */}
                        {venueResults.length > 0 && (
                          <>
                            <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">Your venues</div>
                            {venueResults.map((v, idx) => (
                              <button
                                key={`local-${v.name}-${v.city}-${idx}`}
                                type="button"
                                onPointerDown={(e) => {
                                  e.preventDefault();
                                  setFormData(prev => ({
                                    ...prev,
                                    venue_name: v.name,
                                    city: v.city || prev.city,
                                    venue_id: v.venue_id || prev.venue_id,
                                  }));
                                  setShowVenueDropdown(false);
                                  setShowAddVenueForm(false);
                                  setVenueQuery("");
                                  toast.success(`Selected ${v.name}${v.city ? ' — ' + v.city : ''}`);
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-secondary text-sm flex items-center justify-between"
                              >
                                <span>
                                  <span className="font-medium">{v.name}</span>
                                  {v.city && <span className="text-muted-foreground ml-1">— {v.city}</span>}
                                </span>
                              </button>
                            ))}
                          </>
                        )}
                        {/* Google Places results */}
                        {placesResults.length > 0 && (
                          <>
                            <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">Google Places</div>
                            {placesResults.map((p, idx) => (
                              <button
                                key={`place-${p.place_id}-${idx}`}
                                type="button"
                                onPointerDown={(e) => { e.preventDefault(); handleSelectPlace(p); }}
                                className="w-full text-left px-3 py-2 hover:bg-secondary text-sm"
                                data-testid={`place-result-${idx}`}
                              >
                                <span className="font-medium">{p.main_text}</span>
                                <span className="text-muted-foreground ml-1 text-xs">— {p.secondary_text}</span>
                              </button>
                            ))}
                          </>
                        )}
                        {/* Add new venue option */}
                        {venueQuery.length >= 2 && (
                          <button
                            type="button"
                            onPointerDown={(e) => { e.preventDefault(); handleOpenAddVenueForm(); }}
                            className="w-full text-left px-3 py-2.5 hover:bg-primary/10 text-sm flex items-center gap-2 border-t border-border text-primary font-medium"
                            data-testid="add-new-venue-option"
                          >
                            <Plus className="h-4 w-4" />
                            Add "{venueQuery}" as a new venue
                          </button>
                        )}
                      </div>
                    )}
                    {venueSearching && (
                      <span className="absolute right-3 top-9">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </span>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="call_time">Call Time</Label>
                    <Input
                      id="call_time"
                      value={formData.call_time}
                      onChange={(e) => setFormData({ ...formData, call_time: e.target.value })}
                      onBlur={(e) => {
                        const formatted = formatTimeInput(e.target.value);
                        if (formatted !== e.target.value) {
                          setTimeout(() => {
                            setFormData(prev => ({ ...prev, call_time: formatted }));
                          }, 0);
                        }
                      }}
                      data-testid="input-call-time"
                    />
                  </div>
                </div>

                {/* Inline Add Venue Form */}
                {showAddVenueForm && (
                  <div className="border border-primary/30 rounded-lg p-4 bg-primary/5 space-y-3" data-testid="add-venue-form">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium flex items-center gap-2">
                        <Plus className="h-4 w-4 text-primary" /> New venue
                      </h4>
                      <button type="button" onClick={() => { setShowAddVenueForm(false); setAddVenueDuplicateWarning(null); }} className="text-xs text-muted-foreground hover:text-foreground">Cancel</button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Venue name *</Label>
                        <Input value={addVenueData.name} onChange={e => setAddVenueData(p => ({ ...p, name: e.target.value }))} data-testid="add-venue-name" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Town / City *</Label>
                        <Input value={addVenueData.city} onChange={e => setAddVenueData(p => ({ ...p, city: e.target.value }))} data-testid="add-venue-city" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Postcode</Label>
                        <Input value={addVenueData.postcode} onChange={e => setAddVenueData(p => ({ ...p, postcode: e.target.value }))} data-testid="add-venue-postcode" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Country</Label>
                        <Input value={addVenueData.country} onChange={e => setAddVenueData(p => ({ ...p, country: e.target.value }))} data-testid="add-venue-country" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Capacity</Label>
                        <Input type="number" value={addVenueData.capacity} onChange={e => setAddVenueData(p => ({ ...p, capacity: e.target.value }))} data-testid="add-venue-capacity" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Website</Label>
                        <Input type="url" value={addVenueData.website} onChange={e => setAddVenueData(p => ({ ...p, website: e.target.value }))} placeholder="https://..." data-testid="add-venue-website" />
                      </div>
                    </div>
                    {addVenueDuplicateWarning && (
                      <div className="flex items-center justify-between gap-3 p-3 rounded bg-yellow-500/10 border border-yellow-500/20 text-sm" data-testid="venue-duplicate-warning">
                        <p className="text-yellow-200">A venue with this name already exists at this postcode — did you mean <strong>{addVenueDuplicateWarning.name}</strong> ({addVenueDuplicateWarning.city})?</p>
                        <Button type="button" variant="outline" size="sm" onClick={() => handleUseExistingVenue(addVenueDuplicateWarning)} data-testid="use-existing-venue-btn">Use existing</Button>
                      </div>
                    )}
                    <div className="flex gap-2 pt-1">
                      <Button type="button" variant="ghost" size="sm" onClick={() => { setShowAddVenueForm(false); setAddVenueDuplicateWarning(null); }}>Cancel</Button>
                      <Button type="button" size="sm" disabled={addVenueSaving || !addVenueData.name.trim() || !addVenueData.city.trim()} onClick={handleAddVenueSubmit} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-venue-submit">
                        {addVenueSaving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                        Add venue
                      </Button>
                    </div>
                  </div>
                )}

                {/* Location */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">Town / City *</Label>
                    <ClearableInput
                      id="city"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      required
                      data-testid="input-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">Country</Label>
                    <ClearableInput
                      id="country"
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      data-testid="input-country"
                    />
                  </div>
                </div>

                {/* Fee */}
                <div className="space-y-2">
                  <Label htmlFor="fee">Fee / Payment</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                    <Input
                      id="fee"
                      type="number"
                      step="0.01"
                      value={formData.fee}
                      onChange={(e) => setFormData({ ...formData, fee: e.target.value })}
                      className="pl-7"
                      data-testid="input-fee"
                    />
                  </div>
                </div>

                {/* Dress Code */}
                <div className="space-y-2">
                  <Label htmlFor="dress_code">Dress Code</Label>
                  <Input
                    id="dress_code"
                    value={formData.dress_code}
                    onChange={(e) => setFormData({ ...formData, dress_code: e.target.value })}
                    data-testid="input-dress-code"
                  />
                </div>

                {/* Status & Gig Type */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) => setFormData({ ...formData, status: value })}
                    >
                      <SelectTrigger data-testid="select-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Gig Type</Label>
                    <Select
                      value={formData.gig_type}
                      onValueChange={(value) => setFormData({
                        ...formData,
                        gig_type: value,
                        is_public: value === "public"  // Auto-set visibility
                      })}
                    >
                      <SelectTrigger data-testid="select-gig-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="public">Public Gig</SelectItem>
                        <SelectItem value="function">Private Function</SelectItem>
                        <SelectItem value="session">Studio Session</SelectItem>
                        <SelectItem value="rehearsal">Rehearsal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                  </div>

                  {/* Right column — secondary info */}
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: tokens.space.md,
                    ...(isWideForm ? {
                      padding: 16,
                      background: tokens.color.cardBg,
                      border: tokens.border.card,
                      borderRadius: tokens.radius.lg,
                    } : {}),
                  }}>
                <h3 className="text-sm font-medium mb-4" style={{ color: tokens.color.orange }}>On the Day</h3>

                {/* Every "On the Day" section is a draggable, collapsible pill.
                    Reorder via the grip handle only (lockable); the title
                    toggles open/closed. Order + open state persist per-user in
                    localStorage. */}
                <DndContext sensors={pillSensors} collisionDetection={closestCenter} onDragEnd={handlePillDragEnd}>
                  <SortableContext items={pillOrder} strategy={verticalListSortingStrategy}>
                    {pillOrder.map((pid) => (
                      <SortablePill key={pid} id={pid} open={!!openPills[pid]} onToggle={() => togglePill(pid)}>

                {/* Schedule — running order for the day. Additive: persists as
                    formData.schedule (array of { label, time }). */}
                {pid === "schedule" && (
                    <>
                      <p className="text-xs text-muted-foreground">
                        Running order for the day. Tap a preset to add a row, then set its time.
                      </p>
                      <div className="flex flex-wrap gap-2.5">
                    {SCHEDULE_PRESETS.map((preset) => (
                      <Button
                        key={preset}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            schedule: [...(prev.schedule || []), { label: preset, time: "" }],
                          }))
                        }
                        data-testid={`schedule-preset-${preset.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <Plus className="w-3 h-3 mr-1" /> {preset}
                      </Button>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          schedule: [...(prev.schedule || []), { label: "", time: "" }],
                        }))
                      }
                      data-testid="schedule-preset-custom"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Custom
                    </Button>
                  </div>

                      {(formData.schedule || []).length > 0 && (
                        <div className="space-y-3 pt-2">
                          {(formData.schedule || []).map((row, idx) => (
                            <div key={idx} className="flex items-center gap-3" data-testid={`schedule-row-${idx}`}>
                              <Input
                                value={row.time || ""}
                                placeholder="HH:MM"
                                onChange={(e) =>
                                  setFormData((prev) => {
                                    const schedule = [...(prev.schedule || [])];
                                    schedule[idx] = { ...schedule[idx], time: e.target.value };
                                    return { ...prev, schedule };
                                  })
                                }
                                onBlur={(e) => {
                                  const formatted = formatTimeInput(e.target.value);
                                  if (formatted !== e.target.value) {
                                    setFormData((prev) => {
                                      const schedule = [...(prev.schedule || [])];
                                      schedule[idx] = { ...schedule[idx], time: formatted };
                                      return { ...prev, schedule };
                                    });
                                  }
                                }}
                                className="w-24 shrink-0"
                                data-testid={`schedule-time-${idx}`}
                              />
                              <Input
                                value={row.label || ""}
                                placeholder="Label (e.g. Soundcheck)"
                                onChange={(e) =>
                                  setFormData((prev) => {
                                    const schedule = [...(prev.schedule || [])];
                                    schedule[idx] = { ...schedule[idx], label: e.target.value };
                                    return { ...prev, schedule };
                                  })
                                }
                                className="flex-1"
                                data-testid={`schedule-label-${idx}`}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    schedule: (prev.schedule || []).filter((_, i) => i !== idx),
                                  }))
                                }
                                data-testid={`schedule-remove-${idx}`}
                                aria-label="Remove schedule row"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                )}

                {pid === "logistics" && (
                    <>
                      <p className="text-xs text-muted-foreground">
                        Band accommodation and meal allowances
                      </p>

                      {/* Accommodation */}
                  <div className="border border-border rounded-lg p-3 space-y-2 bg-secondary/10">
                    <Label className="text-xs font-medium">Accommodation</Label>
                    <Input
                      placeholder="Hotel name"
                      value={formData.accommodation?.name || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          accommodation: { ...prev.accommodation, name: e.target.value },
                        }))
                      }
                      data-testid="input-accommodation-name"
                    />
                    <Input
                      placeholder="Google Maps link"
                      value={formData.accommodation?.maps_link || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          accommodation: { ...prev.accommodation, maps_link: e.target.value },
                        }))
                      }
                      data-testid="input-accommodation-maps"
                    />
                    <Input
                      placeholder="Distance from venue (e.g. 15 mins by car)"
                      value={formData.accommodation?.distance_from_venue || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          accommodation: { ...prev.accommodation, distance_from_venue: e.target.value },
                        }))
                      }
                      data-testid="input-accommodation-distance"
                    />
                    <Textarea
                      placeholder="Notes (pool available, etc.)"
                      value={formData.accommodation?.notes || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          accommodation: { ...prev.accommodation, notes: e.target.value },
                        }))
                      }
                      className="min-h-20"
                      data-testid="input-accommodation-notes"
                    />
                  </div>

                  {/* Per Diems */}
                  <div className="border border-border rounded-lg p-3 space-y-2 bg-secondary/10">
                    <Label className="text-xs font-medium">Per Diems</Label>
                    <p className="text-xs text-muted-foreground">Set amount per meal and quantity. Leave blank if provided or not claimed.</p>
                    <div className="space-y-2">
                      {['breakfast', 'lunch', 'dinner'].map((mealType) => (
                        <div key={mealType} className="flex items-end gap-2">
                          <div className="flex-1">
                            <Label className="text-xs capitalize">{mealType}</Label>
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="15.00"
                                value={formData.per_diems?.[mealType]?.value || ""}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    per_diems: {
                                      ...prev.per_diems,
                                      [mealType]: { ...prev.per_diems?.[mealType], value: e.target.value },
                                    },
                                  }))
                                }
                                className="pl-5"
                                data-testid={`input-${mealType}-value`}
                              />
                            </div>
                          </div>
                          <div className="flex-1">
                            <Label className="text-xs">Qty</Label>
                            <div className="relative">
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">×</span>
                              <Input
                                type="number"
                                placeholder="1"
                                value={formData.per_diems?.[mealType]?.quantity || ""}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    per_diems: {
                                      ...prev.per_diems,
                                      [mealType]: { ...prev.per_diems?.[mealType], quantity: e.target.value },
                                    },
                                  }))
                                }
                                className="pr-5 text-center"
                                data-testid={`input-${mealType}-quantity`}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {(() => {
                      const pdTotal = ['breakfast', 'lunch', 'dinner'].reduce((sum, meal) => {
                        const val = parseFloat(formData.per_diems?.[meal]?.value) || 0;
                        const qty = parseInt(formData.per_diems?.[meal]?.quantity) || 1;
                        return sum + (val * qty);
                      }, 0);
                      return pdTotal > 0 ? (
                        <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-secondary/50">
                          <input
                            type="checkbox"
                            checked={addPerDiemsToInvoice}
                            onChange={(e) => setAddPerDiemsToInvoice(e.target.checked)}
                            className="rounded"
                          />
                          <span className="text-xs">Add total (£{pdTotal.toFixed(2)}) to invoice</span>
                        </label>
                      ) : null;
                    })()}
                    </div>
                    </>
                )}

                {pid === "contacts" && (
                    <>
                {/* Collaborators - search bar */}
                  <div className="space-y-2" data-testid="gig-collaborators-section">
                    <Label>Musicians on this gig</Label>

                    {/* Selected musicians - display only */}
                    {(formData.collaborator_ids || []).length > 0 && (
                      <div className="space-y-1 border border-border rounded-lg p-3 bg-secondary/20">
                        <p className="text-xs font-medium text-muted-foreground mb-2">Musicians on this gig</p>
                        {(formData.collaborator_ids || []).map(collabId => {
                          const collab = collaborators.find(c => c.collaborator_id === collabId);
                          if (!collab) return null;

                          return (
                            <div key={collabId} className="flex items-center justify-between px-3 py-1.5 rounded bg-background border border-border/50 text-sm">
                              <span className="flex items-center gap-2">
                                {collab.name}
                                {(() => {
                                  const inv = (formData.invitations || []).find(i => i.collaborator_id === collabId);
                                  if (inv) {
                                    return <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{inv.status === "confirmed" ? "confirmed" : inv.status === "invited" ? "invited" : "invite pending"}</span>;
                                  }
                                  if (mode !== "edit") return null;
                                  return (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setFormData(prev => ({
                                          ...prev,
                                          invitations: [
                                            ...(prev.invitations || []),
                                            { collaborator_id: collabId, collaborator_name: collab.name, email: collab.email || "", status: "pending" }
                                          ]
                                        }));
                                        toast.success(collab.email ? `${collab.name} added to invitations` : `${collab.name} added to invitations - add an email below to send`);
                                      }}
                                      className="text-xs text-yellow-600 hover:underline"
                                      data-testid={`invite-musician-${collabId}`}
                                    >
                                      Invite
                                    </button>
                                  );
                                })()}
                              </span>
                              {mode === "edit" && (
                                <button
                                  type="button"
                                  onClick={() => setFormData(prev => ({
                                    ...prev,
                                    collaborator_ids: (prev.collaborator_ids || []).filter(id => id !== collabId),
                                    invitations: (prev.invitations || []).filter(inv => inv.collaborator_id !== collabId)
                                  }))}
                                  className="text-xs text-muted-foreground hover:text-destructive"
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Band members — live from the band, so members added in Scene later still appear.
                        Lists everyone not yet on the gig; an email is only needed to invite. */}
                    {(() => {
                      const band = bands.find(x => x.band_id === formData.band_id);
                      if (!band) return null;
                      const onGig = new Set(formData.collaborator_ids || []);
                      const available = (band.members || []).filter(m => !onGig.has(m.collaborator_id));
                      if (available.length === 0) return null;
                      return (
                        <div className="space-y-2 border border-border rounded-lg p-3 bg-secondary/20" data-testid="band-members-picker">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-medium text-muted-foreground">Add from {band.name}</p>
                            {available.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  available.forEach(addBandMemberToGig);
                                  toast.success(`${available.length} band members added to this gig`);
                                }}
                                className="text-xs text-primary hover:underline"
                                data-testid="band-add-all"
                              >
                                Add all
                              </button>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {available.map(m => (
                              <button
                                key={m.collaborator_id}
                                type="button"
                                onClick={() => {
                                  addBandMemberToGig(m);
                                  toast.success(`${m.name} added to this gig`);
                                }}
                                className="text-xs px-2.5 py-1 rounded-full border border-border bg-background hover:bg-secondary"
                                data-testid={`band-member-${m.collaborator_id}`}
                              >
                                + {m.name}{m.instrument ? ` (${m.instrument})` : ""}
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Search input */}
                    <Input
                      placeholder="Type to search musicians..."
                      value={collabSearchQuery}
                      onChange={(e) => setCollabSearchQuery(e.target.value)}
                      data-testid="collab-search"
                    />
                    
                    {/* Search results dropdown */}
                    {collabSearchQuery.length >= 2 && (
                      <div className="border rounded-lg max-h-80 overflow-y-auto bg-background">
                        {collaborators
                          .filter(c =>
                            c.name.toLowerCase().includes(collabSearchQuery.toLowerCase()) &&
                            !(formData.collaborator_ids || []).includes(c.collaborator_id)
                          )
                          .slice(0, 10)
                          .map(collab => {
                            const hasEmail = collab.email || collabEmailInputs[collab.collaborator_id];
                            const emailValue = collabEmailInputs[collab.collaborator_id] || collab.email || "";

                            return (
                            <div
                              key={collab.collaborator_id}
                              className="w-full text-left px-3 py-2 hover:bg-secondary flex flex-col gap-2 text-sm border-b last:border-b-0"
                            >
                              <div className="flex items-center justify-between">
                                <span>
                                  {collab.name}
                                  {collab.instrument && <span className="text-muted-foreground ml-1">({collab.instrument})</span>}
                                </span>
                                <div className="flex gap-2">
                                  {mode === "edit" && hasEmail && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setFormData(prev => ({
                                          ...prev,
                                          invitations: [
                                            ...(prev.invitations || []),
                                            {
                                              collaborator_id: collab.collaborator_id,
                                              email: emailValue,
                                              status: "pending"
                                            }
                                          ]
                                        }));
                                        toast.success(`${collab.name} added to invitations`);
                                        setCollabEmailInputs(prev => {
                                          const updated = { ...prev };
                                          delete updated[collab.collaborator_id];
                                          return updated;
                                        });
                                      }}
                                      className="text-xs text-yellow-600 hover:underline"
                                    >
                                      Invite
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData(prev => ({
                                        ...prev,
                                        // On the gig (used for social tags); inviting is a separate step.
                                        collaborator_ids: [...(prev.collaborator_ids || []), collab.collaborator_id],
                                      }));
                                      addCollaboratorToBand(collab.collaborator_id);
                                      setCollabSearchQuery("");
                                      setCollabEmailInputs(prev => {
                                        const updated = { ...prev };
                                        delete updated[collab.collaborator_id];
                                        return updated;
                                      });
                                      toast.success(`${collab.name} added to this gig`);
                                    }}
                                    className="text-primary hover:underline text-xs"
                                  >
                                    + Add
                                  </button>
                                </div>
                              </div>
                            </div>
                            );
                          })}
                        {collaborators.filter(c => 
                          c.name.toLowerCase().includes(collabSearchQuery.toLowerCase()) &&
                          !(formData.collaborator_ids || []).includes(c.collaborator_id)
                        ).length === 0 && (
                          <div className="px-3 py-2 text-sm text-muted-foreground flex items-center gap-2">
                            <span>No matches.</span>
                            <button
                              type="button"
                              disabled={collabAdding}
                              className="text-primary hover:underline disabled:opacity-50"
                              onClick={async () => {
                                setCollabAdding(true);
                                try {
                                  const name = collabSearchQuery.trim();
                                  const res = await apiClient.post("/collaborators", { name });
                                  setCollaborators(prev => [...prev, res.data]);
                                  setFormData(prev => ({
                                    ...prev,
                                    collaborator_ids: [...(prev.collaborator_ids || []), res.data.collaborator_id],
                                  }));
                                  addCollaboratorToBand(res.data.collaborator_id);
                                  setCollabSearchQuery("");
                                  toast.success(`Added "${name}" to this gig`);
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || "Failed to add");
                                } finally {
                                  setCollabAdding(false);
                                }
                              }}
                              data-testid="collab-quick-add"
                            >
                              {collabAdding ? "Adding..." : `+ Add "${collabSearchQuery.trim()}"`}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                {/* Invitations card — edit mode only, persists with gig save */}
                {mode === "edit" && (
                  <div className="space-y-4 pt-2 border-t border-violet-900/10">
                    <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Availability Invitations</h4>

                    {/* Pending */}
                    {(formData.invitations || []).filter(i => i.status === "pending").length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground">
                          Pending ({(formData.invitations || []).filter(i => i.status === "pending").length})
                        </p>
                        {(formData.invitations || []).filter(i => i.status === "pending").map((inv) => {
                          const actualIdx = (formData.invitations || []).findIndex(i => i.collaborator_id === inv.collaborator_id && i.email === inv.email);
                          const emailValue = inv.email || "";
                          const hasEmail = emailValue.trim().length > 0;
                          return (
                            <div key={`${inv.collaborator_id}-${actualIdx}`} className="flex items-center gap-2 px-3 py-2 rounded-md bg-secondary/30">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium">{inv.collaborator_name || inv.email}</p>
                                {mode === "edit" && (
                                  <input
                                    type="email"
                                    placeholder="Add email to invite..."
                                    value={emailValue}
                                    onChange={(e) => {
                                      setFormData(prev => ({
                                        ...prev,
                                        invitations: prev.invitations.map((i, id) =>
                                          id === actualIdx ? { ...i, email: e.target.value } : i
                                        )
                                      }));
                                    }}
                                    className="mt-1 w-full text-xs px-2 py-1 border border-input rounded bg-background"
                                  />
                                )}
                              </div>
                              <div className="flex gap-1 flex-shrink-0">
                                {mode === "edit" && hasEmail && (
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      try {
                                        await apiClient.post(`/gigs/${id}/invite`, {
                                          email: emailValue,
                                          collaborator_id: inv.collaborator_id || null
                                        });
                                        setFormData(prev => ({
                                          ...prev,
                                          invitations: prev.invitations.map((i, idx) =>
                                            idx === actualIdx ? { ...i, status: "invited" } : i
                                          )
                                        }));
                                        toast.success(`Invitation sent to ${emailValue}`);
                                      } catch (error) {
                                        if (error.response?.status === 409) {
                                          toast.error("This email has already been invited to this gig");
                                        } else {
                                          toast.error("Failed to send invitation");
                                        }
                                      }
                                    }}
                                    className="text-xs px-2 py-1 bg-green-600 text-white rounded hover:bg-green-700"
                                  >
                                    Invite
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setFormData(prev => ({
                                      ...prev,
                                      invitations: prev.invitations.filter((_, i) => i !== actualIdx)
                                    }));
                                    toast.success("Removed from invitations");
                                  }}
                                  className="text-muted-foreground hover:text-destructive text-xs"
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Invited - Awaiting Response */}
                    {(formData.invitations || []).filter(i => i.status === "invited").length > 0 && (
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-yellow-600">
                          Awaiting Response ({(formData.invitations || []).filter(i => i.status === "invited").length})
                        </p>
                        {(formData.invitations || []).filter(i => i.status === "invited").map((inv) => {
                          const actualIdx = (formData.invitations || []).findIndex(i => i.email === inv.email && i.status === "invited");
                          return (
                            <div key={`${inv.email}-invited`} className="flex items-center justify-between px-3 py-1.5 rounded-md bg-yellow-500/10 text-sm">
                              <span>{inv.collaborator_name || inv.email}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-yellow-600 text-xs">Invited</span>
                                {mode === "edit" && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        try {
                                          await apiClient.post(`/gigs/${id}/invite`, {
                                            email: inv.email,
                                            collaborator_id: inv.collaborator_id || null
                                          });
                                          toast.success(`Resent invitation to ${inv.email}`);
                                        } catch (error) {
                                          if (error.response?.status === 409) {
                                            toast.error("Already invited");
                                          } else {
                                            toast.error("Failed to resend invitation");
                                          }
                                        }
                                      }}
                                      className="text-muted-foreground hover:text-primary text-xs"
                                      title="Resend invitation"
                                    >
                                      ↻
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setFormData(prev => ({
                                          ...prev,
                                          invitations: prev.invitations.filter((_, i) => i !== actualIdx)
                                        }));
                                        toast.success("Removed from invitations");
                                      }}
                                      className="text-muted-foreground hover:text-destructive text-xs"
                                    >
                                      ×
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Confirmed */}
                    {(formData.invitations || []).filter(i => i.status === "confirmed").length > 0 && (
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-green-600">
                          Confirmed ({(formData.invitations || []).filter(i => i.status === "confirmed").length})
                        </p>
                        {(formData.invitations || []).filter(i => i.status === "confirmed").map((inv) => {
                          const actualIdx = (formData.invitations || []).findIndex(i => i.email === inv.email && i.status === "confirmed");
                          return (
                            <div key={`${inv.email}-confirmed`} className="flex items-center justify-between px-3 py-1.5 rounded-md bg-green-500/10 text-sm">
                              <span>{inv.collaborator_name || inv.email}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-green-600 text-xs">Confirmed ✓</span>
                                {mode === "edit" && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData(prev => ({
                                        ...prev,
                                        invitations: prev.invitations.filter((_, i) => i !== actualIdx)
                                      }));
                                      toast.success("Removed from invitations");
                                    }}
                                    className="text-muted-foreground hover:text-destructive text-xs"
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Declined */}
                    {(formData.invitations || []).filter(i => i.status === "declined").length > 0 && (
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-destructive">
                          Declined ({(formData.invitations || []).filter(i => i.status === "declined").length})
                        </p>
                        {(formData.invitations || []).filter(i => i.status === "declined").map((inv) => {
                          const actualIdx = (formData.invitations || []).findIndex(i => i.email === inv.email && i.status === "declined");
                          return (
                            <div key={`${inv.email}-declined`} className="flex items-center justify-between px-3 py-1.5 rounded-md bg-destructive/10 text-sm">
                              <span>{inv.collaborator_name || inv.email}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-destructive text-xs">Declined</span>
                                {mode === "edit" && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFormData(prev => ({
                                        ...prev,
                                        invitations: prev.invitations.filter((_, i) => i !== actualIdx)
                                      }));
                                      toast.success("Removed from invitations");
                                    }}
                                    className="text-muted-foreground hover:text-destructive text-xs"
                                  >
                                    ×
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {(formData.invitations || []).length === 0 && (
                      <p className="text-xs text-muted-foreground">No invitations yet. Click "Invite" on a contact above.</p>
                    )}
                  </div>
                )}
                    </>
                )}

                {pid === "transport" && (
                    <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="transport_type">Transport</Label>
                    <select
                      id="transport_type"
                      value={formData.transport_type || ""}
                      onChange={(e) => {
                        const transportType = e.target.value;
                        setFormData({ ...formData, transport_type: transportType });
                        // Going by train usually means driving to the station —
                        // point the mileage calculator at the first regular place.
                        if (transportType === "public_transport" && regularPlaces.length > 0 && !parsePlacePhase(mileagePhase)) {
                          setMileagePhase(`place_rt:${regularPlaces[0].id}`);
                          setAddMileageToInvoice(false);
                          setMileageInvoiceConfirmed(false);
                        }
                      }}
                      className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="select-transport"
                    >
                      <option value="">Not specified</option>
                      <option value="driving">🚗 Driving</option>
                      <option value="public_transport">🚂 Public Transport</option>
                      <option value="pickup">🚐 Pickup (tour bus, transfer)</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="transport_notes">Transport Notes</Label>
                    <Input
                      id="transport_notes"
                      value={formData.transport_notes || ""}
                      onChange={(e) => setFormData({ ...formData, transport_notes: e.target.value })}
                      data-testid="input-transport-notes"
                    />
                  </div>
                </div>

                {/* Mileage calculator — shown whenever transport is Driving,
                    or Public Transport when the user has regular places
                    (e.g. driving to the station).
                    Lives here (not Revenue) since it's conceptually
                    transport, even though its totals still feed the
                    invoice-summary calc back in Revenue via shared state
                    (mileageInput/mileagePhase/addMileageToInvoice). */}
                {(formData.transport_type === 'driving' || (formData.transport_type === 'public_transport' && regularPlaces.length > 0)) && (() => {
                  const onewayMiles = parseFloat(mileageInput) || 0;
                  const totalMiles = parseFloat((onewayMiles * mileagePhaseMultiplier(mileagePhase)).toFixed(1));
                  const mileageCost = parseFloat((totalMiles * HMRC_MILEAGE_RATE).toFixed(2));
                  const phaseLabels = {
                    round_trip: "Round trip (home ↔ venue)", outbound: "Home → venue", return: "Venue → home",
                    between: "Between venues", airport_round_trip: "Round trip (home ↔ airport)",
                    airport_outbound: "Home → airport", airport_return: "Airport → home",
                  };
                  const phaseLabel = (phase, claimLabel) => {
                    const placePhase = parsePlacePhase(phase);
                    if (!placePhase) return phaseLabels[phase] || phaseLabels.round_trip;
                    const place = regularPlaces.find(p => p.id === placePhase.placeId);
                    if (!place && claimLabel) return claimLabel;
                    const name = place?.name || "regular place";
                    if (placePhase.leg === "out") return `Home → ${name}`;
                    if (placePhase.leg === "ret") return `${name} → home`;
                    return `Round trip (home ↔ ${name})`;
                  };
                  // A saved leg whose place was since removed from Settings.
                  const orphanPlacePhase = parsePlacePhase(mileagePhase)
                    && !regularPlaces.some(p => p.id === parsePlacePhase(mileagePhase).placeId);
                  // Already-logged claim for the phase currently selected
                  const existingForPhase = gigMileageClaims.find(c => (c.phase || "round_trip") === mileagePhase);
                  const choosePhase = (phase) => {
                    setMileagePhase(phase);
                    setMileageInput(mileagePhaseDistance(phase, formData, regularPlaces));
                    setMileageLogged(false);
                    setAddMileageToInvoice(false);
                    setMileageInvoiceConfirmed(false);
                  };

                  return (
                    <div className="p-3 rounded-lg border border-border bg-secondary/20 space-y-3">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Car className="h-3.5 w-3.5 shrink-0" />
                          <span className="font-medium text-foreground">Mileage calculator</span>
                          {formData.estimated_distance_miles > 0 && (
                            <span className="text-muted-foreground">(estimated)</span>
                          )}
                        </div>
                        {/* Journey phase — pick which leg this mileage covers.
                            Fly-out gigs (is_multiday) add home↔airport legs.
                            Switching phase reloads the one-way distance from
                            the matching estimate (venue vs airport legs use
                            different distances; "between" has none). */}
                        <div className="flex gap-2 flex-wrap">
                          <select
                            value={phaseWhere(mileagePhase)}
                            onChange={e => choosePhase(composePhase(e.target.value, phaseDirection(mileagePhase)))}
                            className="text-xs h-7 max-w-full rounded-md border border-input bg-background px-2"
                            data-testid="mileage-where"
                          >
                            <option value="venue">Gig venue</option>
                            {formData.is_multiday && <option value="airport">Airport</option>}
                            {regularPlaces.map(p => (
                              <option key={p.id} value={`place:${p.id}`}>{p.name}</option>
                            ))}
                            {orphanPlacePhase && (
                              <option value={phaseWhere(mileagePhase)}>{phaseLabel(mileagePhase)}</option>
                            )}
                          </select>
                          <select
                            value={phaseDirection(mileagePhase)}
                            onChange={e => choosePhase(composePhase(phaseWhere(mileagePhase), e.target.value))}
                            className="text-xs h-7 max-w-full rounded-md border border-input bg-background px-2"
                            data-testid="mileage-direction"
                          >
                            <option value="round_trip">Round trip</option>
                            <option value="outbound">One way out</option>
                            <option value="return">One way back</option>
                            {phaseWhere(mileagePhase) === "venue" && <option value="between">Between venues</option>}
                          </select>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="relative w-28">
                          <Input
                            type="number"
                            min="0"
                            step="0.1"
                            placeholder="Miles"
                            value={mileageInput}
                            onChange={e => {
                              setMileageInput(e.target.value);
                              setMileageLogged(false);
                              setAddMileageToInvoice(false);
                              setMileageInvoiceConfirmed(false);
                            }}
                            className="pr-10 text-sm h-8"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">mi</span>
                        </div>
                        {mileagePhaseMultiplier(mileagePhase) === 2 && onewayMiles > 0 && (
                          <span className="text-xs text-muted-foreground">× 2 = {totalMiles} mi total</span>
                        )}
                        {totalMiles > 0 && (
                          <>
                            <span className="text-sm text-muted-foreground">× 45p/mile</span>
                            <span className="text-sm font-medium text-foreground">= £{mileageCost.toFixed(2)}</span>
                          </>
                        )}
                      </div>

                      {totalMiles > 0 && (
                        <div className="flex items-center gap-2 flex-wrap">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-xs h-7"
                            disabled={mileageLogged || mileageLogging}
                            onClick={async () => {
                              setMileageLogging(true);
                              try {
                                const { data: saved } = await apiClient.post("/mileage-claims", {
                                  gig_id: formData.gig_id || null,
                                  band_name: formData.artist_name || null,
                                  date: formData.date ? format(formData.date, "yyyy-MM-dd") : null,
                                  city: formData.city || null,
                                  country: formData.country || null,
                                  miles: totalMiles,
                                  amount_gbp: mileageCost,
                                  phase: mileagePhase,
                                  phase_label: parsePlacePhase(mileagePhase) ? phaseLabel(mileagePhase) : null,
                                });
                                // Replace any existing claim for this phase (upsert), else append
                                setGigMileageClaims(prev => {
                                  const rest = prev.filter(c => (c.phase || "round_trip") !== mileagePhase);
                                  return saved ? [saved, ...rest] : rest;
                                });
                                setMileageLogged(true);
                              } catch (e) {
                                console.error("Failed to log mileage claim", e);
                                toast.error("Failed to log mileage claim");
                              } finally {
                                setMileageLogging(false);
                              }
                            }}
                          >
                            {mileageLogging ? (
                              <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" />{existingForPhase ? "Updating…" : "Logging…"}</>
                            ) : mileageLogged ? (
                              <><Check className="h-3 w-3 mr-1.5" />{existingForPhase ? "Updated" : "Logged"}</>
                            ) : existingForPhase ? (
                              "Update mileage claim"
                            ) : (
                              "Log mileage claim"
                            )}
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-xs h-7"
                            disabled={mileageInvoiceConfirmed}
                            onClick={() => {
                              setAddMileageToInvoice(true);
                              setMileageInvoiceConfirmed(true);
                            }}
                          >
                            {mileageInvoiceConfirmed ? (
                              <><Check className="h-3 w-3 mr-1.5" />Added to invoice</>
                            ) : (
                              `Add mileage (£${mileageCost.toFixed(2)}) to invoice`
                            )}
                          </Button>
                        </div>
                      )}

                      {/* Already-logged legs for this gig */}
                      {gigMileageClaims.length > 0 && (
                        <div className="pt-2 border-t border-border space-y-1.5">
                          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Logged for this gig</p>
                          {gigMileageClaims.map(c => (
                            <div key={c.claim_id} className="flex items-center justify-between gap-2 text-xs">
                              <span className="text-foreground truncate">
                                {phaseLabel(c.phase, c.phase_label)} · {c.miles} mi · £{(c.amount_gbp ?? 0).toFixed(2)}
                              </span>
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    await apiClient.delete(`/mileage-claims/${c.claim_id}`);
                                    setGigMileageClaims(prev => prev.filter(x => x.claim_id !== c.claim_id));
                                    if ((c.phase || "round_trip") === mileagePhase) setMileageLogged(false);
                                  } catch { toast.error("Failed to delete claim"); }
                                }}
                                className="text-muted-foreground hover:text-red-400 transition-colors shrink-0"
                                title="Delete this logged leg"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
                    </>
                )}

                {pid === "tickets" && (<>
                {/* Tickets */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ticket_link">Ticket Link</Label>
                    <ClearableInput
                      id="ticket_link"
                      type="url"
                      value={formData.ticket_link}
                      onChange={(e) => setFormData({ ...formData, ticket_link: e.target.value })}
                      placeholder="https://..."
                      data-testid="input-ticket-link"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ticket_price">Ticket Price</Label>
                    <ClearableInput
                      id="ticket_price"
                      value={formData.ticket_price}
                      onChange={(e) => setFormData({ ...formData, ticket_price: e.target.value })}
                      data-testid="input-ticket-price"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="venue_website">Venue Website</Label>
                  <ClearableInput
                    id="venue_website"
                    type="url"
                    value={formData.venue_website}
                    onChange={(e) => setFormData({ ...formData, venue_website: e.target.value })}
                    data-testid="input-venue-website"
                  />
                </div>
                </>)}

                {pid === "money" && (<>
                {/* Expenses + Payment Status */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="expenses">Expenses</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                      <Input
                        id="expenses"
                        type="number"
                        step="0.01"
                        value={formData.expenses}
                        onChange={(e) => setFormData({ ...formData, expenses: e.target.value })}
                        className="pl-7"
                        data-testid="input-expenses"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Payment Status</Label>
                    <Select
                      value={formData.fee_status}
                      onValueChange={(value) => setFormData({ ...formData, fee_status: value })}
                    >
                      <SelectTrigger data-testid="select-fee-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="invoiced">Invoiced</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Invoice to — per-gig recipient override */}
                {(() => {
                  const gigCollaborators = collaborators.filter(c =>
                    (formData.collaborator_ids || []).includes(c.collaborator_id)
                  );
                  const items = [
                    { value: "auto", label: "Auto (band / venue default)" },
                    ...(formData.venue_id ? [{ value: "venue", label: formData.venue_name ? `Venue: ${formData.venue_name}` : "Invoice venue" }] : []),
                    ...(formData.band_id ? [{ value: "band", label: formData.artist_name ? `Band: ${formData.artist_name}` : "Invoice band" }] : []),
                    ...gigCollaborators.map(c => ({ value: `collaborator:${c.collaborator_id}`, label: c.name })),
                    { value: "custom", label: "Custom contact…" },
                  ];

                  const currentValue = formData.invoice_recipient_type === "collaborator" && formData.invoice_recipient_id
                    ? `collaborator:${formData.invoice_recipient_id}`
                    : (formData.invoice_recipient_type || "auto");

                  const previewLabel = recipientPreview?.name
                    ? `→ ${recipientPreview.name}${recipientPreview.email ? ` (${recipientPreview.email})` : ""}`
                    : null;

                  return (
                    <div className="space-y-2">
                      <Label>Invoice to</Label>
                      <SearchableSelect
                        items={items}
                        value={currentValue}
                        onChange={(val) => {
                          if (val.startsWith("collaborator:")) {
                            setFormData(prev => ({
                              ...prev,
                              invoice_recipient_type: "collaborator",
                              invoice_recipient_id: val.replace("collaborator:", ""),
                              invoice_recipient_name: "",
                              invoice_recipient_email: "",
                            }));
                          } else {
                            setFormData(prev => ({
                              ...prev,
                              invoice_recipient_type: val,
                              invoice_recipient_id: "",
                              invoice_recipient_name: "",
                              invoice_recipient_email: "",
                            }));
                          }
                        }}
                        placeholder="Auto (band / venue default)"
                        searchPlaceholder="Search recipients…"
                      />
                      {previewLabel && (
                        <p className="text-xs text-muted-foreground">{previewLabel}</p>
                      )}
                      {formData.invoice_recipient_type === "custom" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1">
                          <Input
                            placeholder="Contact name"
                            value={formData.invoice_recipient_name}
                            onChange={(e) => setFormData(prev => ({ ...prev, invoice_recipient_name: e.target.value }))}
                          />
                          <Input
                            placeholder="Email (optional)"
                            type="email"
                            value={formData.invoice_recipient_email}
                            onChange={(e) => setFormData(prev => ({ ...prev, invoice_recipient_email: e.target.value }))}
                          />
                        </div>
                      )}
                    </div>
                  );
                })()}

                {(formData.fee || formData.expenses || addPerDiemsToInvoice || addMileageToInvoice) && (() => {
                  const fee = parseFloat(formData.fee) || 0;
                  const manualExpenses = parseFloat(formData.expenses) || 0;
                  const pdTotal = addPerDiemsToInvoice ? ['breakfast', 'lunch', 'dinner'].reduce((sum, meal) => {
                    const val = parseFloat(formData.per_diems?.[meal]?.value) || 0;
                    const qty = parseInt(formData.per_diems?.[meal]?.quantity) || 1;
                    return sum + (val * qty);
                  }, 0) : 0;
                  const onewayMiles = parseFloat(mileageInput) || 0;
                  const totalMiles = parseFloat((onewayMiles * mileagePhaseMultiplier(mileagePhase)).toFixed(1));
                  const mileageCost = addMileageToInvoice ? parseFloat((totalMiles * HMRC_MILEAGE_RATE).toFixed(2)) : 0;
                  const totalInvoiceAmount = fee + pdTotal + mileageCost;
                  const netIncome = totalInvoiceAmount - manualExpenses;

                  return (
                    <div className="p-4 bg-secondary/30 rounded-lg space-y-2">
                      {fee > 0 && <div className="flex justify-between items-center text-sm"><span className="text-muted-foreground">Fee</span><span className="font-mono">£{fee.toFixed(2)}</span></div>}
                      {pdTotal > 0 && <div className="flex justify-between items-center text-sm"><span className="text-muted-foreground">Per Diems</span><span className="font-mono">£{pdTotal.toFixed(2)}</span></div>}
                      {mileageCost > 0 && <div className="flex justify-between items-center text-sm"><span className="text-muted-foreground">Mileage</span><span className="font-mono">£{mileageCost.toFixed(2)}</span></div>}
                      {manualExpenses > 0 && <div className="flex justify-between items-center text-sm"><span className="text-muted-foreground">−&nbsp;Manual Expenses</span><span className="font-mono">−£{manualExpenses.toFixed(2)}</span></div>}
                      <div className="border-t border-border pt-2 flex justify-between items-center">
                        <span className="text-sm font-medium">Net Income</span>
                        <span className={`font-mono font-medium ${netIncome >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          £{netIncome.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  );
                })()}
                </>)}

                {pid === "setlist" && (<>
                {/* Genre */}
                <div className="space-y-2">
                  <Label>Genre</Label>
                  <Select
                    value={formData.genre}
                    onValueChange={(value) => setFormData({ ...formData, genre: value })}
                  >
                    <SelectTrigger data-testid="select-genre">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="jazz">Jazz</SelectItem>
                      <SelectItem value="rock">Rock</SelectItem>
                      <SelectItem value="pop">Pop</SelectItem>
                      <SelectItem value="classical">Classical</SelectItem>
                      <SelectItem value="electronic">Electronic</SelectItem>
                      <SelectItem value="folk">Folk</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <SetlistEditor
                  setlist={formData.setlist || []}
                  onChange={(setlist) => setFormData({ ...formData, setlist })}
                />
                </>)}

                {pid === "notes" && (
                <div className="space-y-2" data-testid="gig-notes-section">
                  <Label htmlFor="gig_notes">Gig Notes</Label>
                  <Textarea
                    id="gig_notes"
                    value={formData.gig_notes}
                    onChange={(e) => setFormData({ ...formData, gig_notes: e.target.value })}
                    rows={2}
                    data-testid="input-gig-notes"
                  />
                </div>
                )}

                      </SortablePill>
                    ))}
                  </SortableContext>
                </DndContext>

                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Share Section - only in edit mode */}
            {mode === "edit" && id && (
              <Card className="bg-card border-border" data-testid="share-gig-section">
                <CardHeader className="p-4 md:p-6">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Share2 className="h-5 w-5 text-primary" /> Share Gig
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4 pt-0 md:p-6 md:pt-0 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Enable Sharing</p>
                      <p className="text-xs text-muted-foreground">Create a read-only link for this gig</p>
                    </div>
                    <Switch
                      checked={shareEnabled}
                      onCheckedChange={handleShareToggle}
                      disabled={shareToggling}
                      data-testid="share-toggle"
                    />
                  </div>

                  {shareEnabled && shareUrl && (
                    <div className="space-y-3 pt-2 border-t border-border">
                      {/* Share link */}
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-secondary/50 rounded-lg px-3 py-2 text-xs font-mono truncate" data-testid="share-link-display">
                          {shareUrl}
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={handleCopyLink}
                          className="shrink-0 gap-1.5"
                          data-testid="copy-share-link-btn"
                        >
                          {linkCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                          {linkCopied ? "Copied" : "Copy"}
                        </Button>
                      </div>

                      {/* Share actions: each opens a WhatsApp / Text choice */}
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          className="flex-1 gap-2"
                          onClick={() => setShareDialog({ gig: { ...formData, gig_id: id, share_enabled: shareEnabled, share_token: shareToken }, kind: "dep" })}
                          data-testid="dep-this-out-btn"
                        >
                          <MessageCircle className="h-4 w-4" />
                          Dep this out
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="flex-1 gap-2"
                          onClick={() => setShareDialog({ gig: { ...formData, gig_id: id, share_enabled: shareEnabled, share_token: shareToken }, kind: "family" })}
                          data-testid="share-gig-btn"
                        >
                          <MessageCircle className="h-4 w-4" />
                          Share gig
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between">
              {mode === "edit" && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => {
                    setGigToDelete(id);
                    setShowDeleteDialog(true);
                  }}
                  data-testid="delete-gig-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Gig
                </Button>
              )}
              <div className={cn("flex gap-3", mode !== "edit" && "ml-auto")}>
                <Button type="button" variant="outline" onClick={() => navigate("/gigs")} data-testid="cancel-btn">
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="bg-primary text-primary-foreground btn-glow" data-testid="save-gig-btn">
                  {submitting ? "Saving..." : (mode === "edit" ? "Save Changes" : "Create Gig")}
                </Button>
              </div>
            </div>
          </form>
        )}

        {/* Delete Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Gig</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this gig? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete">
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      {/* First Gig Success Overlay */}
      {showFirstGigSuccess && mode === "edit" && id && (
        <FirstGigSuccess gigId={id} onDismiss={() => setShowFirstGigSuccess(false)} />
      )}
    </Layout>
  );
};

export default GigManager;
